import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import minBy from 'lodash/minBy';
import { IColorPickerInsuranceProps } from './components/backoffice/RXC_COLOR_PICKER/RXC_COLOR_PICKER';
import DebugConsole from './debugConsole';
import './debugConsole.css';
import { Content, GetOptions, LensPackage } from './meta/IBrain';
import { Parameter, ParameterWithTitle } from './models/Parameter';
import { Workflow } from './models/Workflow';

import { parameterToPlainAdapter } from './adapters/ParameterToPlainAdapter';
import { callInsuranceService } from './modules/RIA/hooks/utils';
import { colorActions } from './redux/slices/color';
import { insuranceActions } from './redux/slices/insurance';
import { prescriptionActions } from './redux/slices/prescription';
import { workflowActions } from './redux/slices/workflow';
import {
  currentCategory,
  filterExistingProperties,
  getFrameOnlyKey,
  getStepSelections,
  getUrlSwatch,
  hasValidPrescription,
  sunRule,
  transitionRule,
  useReleaseVersion,
} from './utils/functions';
import {
  getAddons,
  getBrands,
  getBundles,
  getColorCategories,
  getDesignTypes,
  getFamilyFilter,
  getGVPTreatments,
  getIncluded,
  getOptionsByType,
  getSunColors,
  getThickness,
  getTransitionColors,
  getTreatmentsFamily,
  getTypes,
  loadTreatments,
} from './utils/functions/options';
import {
  getCheapPackageFromList,
  getPriceStep,
  useCurrentPrices,
} from './utils/functions/prices';
import { CurrentPrices, IFlowDefiningAttributes } from './utils/types';

declare global {
  interface Window {
    rxcBrain: any;
    RXC: any;
  }
}
class Brain extends Workflow {
  config?: any;
  dataFrame?: any;
  initialLensPackages?: LensPackage[] | undefined;
  content?: Content;
  warrantyOptions?: any;
  lensType?: string;
  currentCategory?: string;
  allCurrentPackages?: {
    validRXPackages: LensPackage[];
    invalidRXPackages: LensPackage[];
  };
  currentPackages?: LensPackage[];
  currentPrescription: any;
  currentTreatments: any;
  gvpSelectionMade: boolean;
  bundlesEnabled?: boolean;
  showFramePlusLensesPrice?: boolean;
  additionalParameters?: any;
  currentPrices: CurrentPrices;
  selectedWarranty?: any;
  debugConsole?: any;
  packageFromCart?: LensPackage;
  packageFromConfigClen?: LensPackage;
  enablePreselect: boolean;
  insuranceEnabled: boolean;
  bundles: any;
  bundlesPrices: any;
  prismPrice?: any;
  isPrismPriceAdded: boolean;
  mixAndMatchSelections: { [key: string]: string }[];
  speedyRxModule?: {
    listPrice: string;
    offerPrice: string;
    speedyExperience: boolean;
    setActive: (val: boolean) => void;
    isActive: () => boolean;
  };
  showSpeedyInReview: boolean;
  isSpeedyActive: boolean;
  flowDefiningAttributes?: IFlowDefiningAttributes;
  enableGrayout?: boolean;
  prevStepSelectionsMonetate: object[];
  discountMethod: string;
  reviewStepReached: boolean;
  hideCurrentStep: boolean;
  isWaitingForPrescription: boolean;
  notShowFirstComponent?: boolean;
  selectedReviewWarranty?: any;
  URLS?: [];
  superNovaCurrentFilteredPackages?: any;
  selectedFrameColor?: any;
  selectedLensPrice?: any;
  constructor() {
    super();
    this.currentPrices = {
      frame: { price: 0, discount: 0, insPrice: 0 },
      lens: { price: 0, discount: 0, insPrice: 0 },
      total: { price: 0, discount: 0, insPrice: 0 },
    };
    this.currentTreatments = null;
    this.bundlesEnabled = false;
    this.showFramePlusLensesPrice = false;
    this.additionalParameters = null;
    this.selectedWarranty = null;
    this.enablePreselect = false;
    this.insuranceEnabled = false;
    this.prismPrice = 0;
    this.isPrismPriceAdded = false;
    this.prevStepSelectionsMonetate = [];
    this.discountMethod = 'STANDARD';
    this.reviewStepReached = false;
    this.hideCurrentStep = false;
    this.isWaitingForPrescription = true;
    this.notShowFirstComponent = false;
    this.URLS = [];
    this.superNovaCurrentFilteredPackages = [];
    this.selectedFrameColor;
    this.selectedLensPrice;
    this.isSpeedyActive = false;
  }

  // method called to init the Brain with config object
  init(config: any) {
    this.config = config;
    this.content = config?.lensesData?.content;
    this.initialLensPackages = config?.lensesData?.packages;
    this.warrantyOptions = config?.lensesData?.warrantyOptions;
    this.dataFrame = config?.data?.frame;
    this.bundles = config?.lensesData?.bundles;
    this.bundlesPrices = config?.lensesData?.bundlesPrices;
    if (config?.speedyRxModule) {
      this.speedyRxModule = config?.speedyRxModule;
      this.showSpeedyInReview = false;
      this.isSpeedyActive = !!this.speedyRxModule?.isActive();
    }
    this.reviewStepReached = false;
    if (this.initialLensPackages) {
      this.currentCategory = currentCategory(this.initialLensPackages);
      this.getCurrentPackages(false);
      this.getCurrentPackages(true);
    }

    this.#updateCurrentPackagesOnStepChange();

    if (config?.showUI === false) {
      this.#initBrainOnly();
    }
  }

  cleanConfig = () => {
    this.config = {};
  };

  setSelectedFrameColor(frame: any) {
    this.selectedFrameColor = frame;
  }
  setSelectedLensPrice(lensPrice: any) {
    this.selectedLensPrice = lensPrice;
  }
  initWithNewLensesData(config: any) {
    this.config = config;
    this.content = config?.lensesData?.content;
    this.initialLensPackages = config?.lensesData?.packages;
    this.warrantyOptions = config?.lensesData?.warrantyOptions;
    this.dataFrame = config?.data?.frame;

    if (this.initialLensPackages) {
      this.currentCategory = currentCategory(this.initialLensPackages);
      this.getCurrentPackages(false);
      this.getCurrentPackages(true);
    }

    this.debugConsole = new DebugConsole(this);
    this.debugConsole.initDebugConsole();
    this.debugConsole.logCurrentStep();
    this.debugConsole.logFilteredPackages();

    const packageFromCartCatEntryId = config?.data?.lens?.catEntryId;
    const packageFromCartUpc = config?.data?.lens?.upc;
    if (config?.data?.lens?.clen) {
      this.packageFromConfigClen = config?.data?.lens?.clen;
    }
    if (packageFromCartCatEntryId || packageFromCartUpc) {
      this.packageFromCart = this.initialLensPackages?.find(
        (p) =>
          p.lensPackage.catEntryId?.toString() === packageFromCartCatEntryId ||
          p.lensPackage.upc === packageFromCartUpc
      );
      this.setShowSpeedyInReview(
        !!(
          this.showSpeedyInReview &&
          this.packageFromCart?.lensPackage?.isSpeedyRX
        )
      );

      this.enablePreselect = true;
    }
  }
  setNewLensesData(config: any) {
    this.config.lensesData = config.lensesData;
  }
  // brain only functionalities

  #initBrainOnly() {
    console.info('------------------------------');
    console.info('Received config: ', this.config);
    //this.debugConsole.logFilteredPackagesFromURL();
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////

  sendMonetateEvent(currentStepId: string, prescriptionFlow?: string) {
    let prevStepSelectionPrice = 0;

    this.currentStep?.params?.forEach((p) => {
      if (p?.price) {
        if (p?.discount) {
          prevStepSelectionPrice = p.price - p.discount;
        } else {
          prevStepSelectionPrice = p.price;
        }
      }
    });
    //TODO: remove schiantone
    if (currentStepId === 'LensPrescription' && !prescriptionFlow) {
      return;
    }

    // update monetate prev step selections
    if (this.currentStep?.params?.length > 0) {
      this.prevStepSelectionsMonetate.push(...this.currentStep.params);
    }

    let rxcEventObject = {
      detail: {
        frameUpc: this.config.data.frame.upc,
        name: currentStepId,
        currentPrices: this.currentPrices,
        prevStepSelections: [...this.prevStepSelectionsMonetate],
        prevStepSelectionPrice: prevStepSelectionPrice,
      },
    };
    if (currentStepId === 'LensPrescription' && prescriptionFlow) {
      rxcEventObject = {
        detail: {
          ...rxcEventObject.detail,
          flow: prescriptionFlow,
        },
      };
    }
    const rxcEvent = new CustomEvent('RXC-ActiveStep', rxcEventObject);

    console.log('Monetate eventObject: ', rxcEventObject.detail);

    window.dispatchEvent(rxcEvent);
  }

  setSpeedyRXStatus(status: boolean) {
    const setModuleFunction = this.config?.speedyRxModule?.setActive;
    this.isSpeedyActive = status;
    // setto speedy rx con tramite il suo module
    if (setModuleFunction) {
      setModuleFunction(status);
    }
  }

  setShowSpeedyInReview(show: boolean) {
    this.showSpeedyInReview = show;
  }

  setMixAndMatchSelections(selections: { [key: string]: string }[]) {
    this.mixAndMatchSelections = selections;
  }

  setCurrentStepCheapestPackages(pkg: LensPackage) {
    if (this.currentStep) {
      if (
        !this.currentStep.cheapestPackages.find(
          (p) => p.lensPackage.upc === pkg.lensPackage.upc
        )
      ) {
        this.currentStep.cheapestPackages.push(pkg);
      }
    }
  }

  #updateCurrentPackagesOnStepChange() {
    const changeStep = async () => {
      if (this.currentStep) {
        this.getCurrentPackages(false);
        this.getCurrentPackages(true);
        this.currentPrices = useCurrentPrices(
          this.currentPackages,
          this.config
        );

        this.debugConsole?.logCurrentStep();
        this.debugConsole?.logFilteredPackages();
        this.sendMonetateEvent(this.currentStep.key);
      }
    };
    this.newEventEmitter.on('changeStepPrice', changeStep);
    // window.removeEventListener("changeStepPrice", changeStep);
    // window.addEventListener("changeStepPrice", changeStep);
  }

  getFirstProduct() {
    //@ts-ignore
    const products: { [id: string] } = window?.utag_data?.Products;
    if (!products) return null;

    const [first] = Object.keys(products);

    return products[first];
  }

  formatProduct(product) {
    const {
      Cloth,
      MoCo,
      Type,
      LensColor,
      FrameColor,
      Visibility,
      PerkCode,
      Badges,
      IdNormalized,
      PricePerUnits,
      PriceFullPerUnits,
      DiscountPricePerUnits,
      EngravingText,
      ...rest
    } = product;

    rest.FrameType = '';

    rest.SpeedyRx_Eligible = this.speedyRxModule?.isActive() && this.isSpeedyActive ? 'Y' : '';

    return rest;
  }

  performAnalytics() {
    this.pushAnalyticsData();
  }

  pushAnalyticsData() {
    //if (process.env.DEV) return;
    let Order_PrescriptionMethod_placeholder: {};
    let Events_PrescriptionOk_placeholder: {};

    let prevStepChain = this.getPrevAndCurrentChainIgnoreSkip();
    let skippedPrescription = prevStepChain.filter((step) =>
      step?.params?.find(
        (p) => p.value !== 'Progressive' || !p.value.includes('Single Vision')
      )
    );

    let passedPrescriptionStep = prevStepChain.find(
      (ps, index) =>
        ps.key === 'LensPrescription' && index < prevStepChain.length - 1
    );
    const sendingMethod = prevStepChain
      .flatMap((step) => step?.params || [])
      .find(
        (p) => p.attributes?.sendingMethod && p.attributes.sendingMethod !== ''
      )?.attributes.sendingMethod;

    if (passedPrescriptionStep) {
      if (skippedPrescription && !sendingMethod) {
        Order_PrescriptionMethod_placeholder = {
          Order_PrescriptionMethod: 'skipped',
        };
        Events_PrescriptionOk_placeholder = {
          Events_PrescriptionOk: 0,
        };
      } else {
        if (sendingMethod) {
          Order_PrescriptionMethod_placeholder = {
            Order_PrescriptionMethod: sendingMethod,
          };
        }
        if (sendingMethod === 'account') {
          Order_PrescriptionMethod_placeholder = {
            Order_PrescriptionMethod: 'myaccount',
          };
        }
        Events_PrescriptionOk_placeholder = {
          Events_PrescriptionOk: 1,
        };
      }
    }

    const selections = this.getPrevCurrentParameterStep();
    const product = this.getFirstProduct() || {};
    if (selections) {
      if (!selections.length || !selections.find((sel) => sel.key === 'type')) {
        product.Type = 'STD';
      }
      selections.forEach((sel) => {
        switch (sel.key) {
          case 'type':
            let configFrameOnly = getFrameOnlyKey(
              this.currentPackages,
              this.config.data
            );
            if (sel.value === configFrameOnly) {
              product.Type = 'STD';
              product.LensType = 'PLANO';
            } else {
              product.Type = 'RX';
              if (sel.value === 'Non_prescription') {
                product.LensType = 'PLANO';
              } else {
                product.LensType = 'RX';
              }
            }
            break;
          case 'brand':
            product.LensBrand = sel.value;
            product.Brand = sel.value;
            break;
          case 'designType':
            product.LensDesign = sel.value;
            break;
          case 'thickness':
            product.LensMaterial = sel.value;
            break;
          case 'blueLight':
            product.LensAntiblue = sel.value;
            break;
          case 'transition':
            product.LensTransition = sel.value;
            break;
          case 'addOns':
            product.Addon = sel.value?.selection || null;
          case 'color':
            if (sel?.optionalAttributes?.key === 'brand') {
              product.LensBrand = sel?.optionalAttributes.value;
              product.Brand = sel?.optionalAttributes.value;
            }
            product.LensColor = sel.value;
        }
      });
      product.Price = (
        this.isPrismPriceAdded
          ? this.currentPrices.total.price +
            this.prismPrice -
            this.currentPrices.total.discount
          : this.currentPrices.total.price - this.currentPrices.total.discount
      ).toFixed(2);
      product.PriceFull = this.isPrismPriceAdded
        ? this.currentPrices.total.price.toFixed(2) + this.prismPrice
        : this.currentPrices.total.price.toFixed(2);
    }

    let currentPackage = getCheapPackageFromList(this.currentPackages);
    if (currentPackage) {
      product.LensUPC = currentPackage.lensPackage.upc;
    }
    console.log(
      `RXC: push analytics impression for ${this?.currentStep?.analytics_section}`
    );

    try {
      // @ts-ignore
      if (!window.tealium_data2track) return;

      // @ts-ignore
      window.tealium_data2track.push({
        id: 'Impression',
        Rx_Release: useReleaseVersion(),
        Page_Type: '',
        Page_Section1:
          this.config.data.frame.category === 'SUNGLASSES'
            ? 'SunLensPanel'
            : 'LensPanel',
        Page_Section2: this.currentStep?.analytics_section,
        Products: {
          [this.config.data.frame?.upc
            ? this.config.data.frame.upc
            : this.config.data.frame.upc]: this.formatProduct(product),
        },
        Page_Design:
          this.speedyRxModule?.isActive() && this.isSpeedyActive
            ? 'SpeedyRx'
            : '',
        ...Order_PrescriptionMethod_placeholder,
        ...Events_PrescriptionOk_placeholder,
      });
    } catch (error) {
      console.error(error);
    }
  }

  mockInsuranceResponse(
    _selectedFrame: any,
    _selectedPackages: LensPackage[],
    dispatch: any
  ) {
    dispatch(
      insuranceActions.setInsuranceLoading({
        isLoading: true,
        stepKey: window.rxcBrain.currentStep?.key,
      })
    );
    let _insurancePackages = JSON.parse(JSON.stringify(_selectedPackages));

    for (let i = 0; i < _insurancePackages.length; i++) {
      var lensListPrice = parseFloat(
        _insurancePackages[i].lensPackage.listPrice
      );
      _insurancePackages[i].lensPackage.insPrice = parseFloat(
        (lensListPrice - lensListPrice * 0.7).toFixed(2)
      );
      _insurancePackages[i].frame.insPrice = (
        parseFloat(_insurancePackages[i].frame.offerPrice) * 0.7
      ).toFixed(2);
    }
    console.log(_insurancePackages);
    return new Promise((res) => {
      setTimeout(() => {
        res(_insurancePackages);
      }, 3000);
    });
  }

  updatePackagesAndPricesFromRIA(
    response: any,
    dispatch: any,
    colorPickerProps?: IColorPickerInsuranceProps
  ) {
    // update initial packages

    this.initialLensPackages = this.initialLensPackages?.map((p) => {
      const findedByUpc = response.find(
        (insPackage) =>
          insPackage.lensPackage &&
          insPackage.lensPackage.upc === p.lensPackage.upc
      );
      if (findedByUpc) return findedByUpc;
      return p;
    });

    // update current step cheapest packages
    // Se viene dal color picker l'aggiungo direttamente ai cheapest
    if (colorPickerProps && response.length === 1) {
      this.currentStep.cheapestPackages.push(response[0]);
      const [colors, _] = this.getTransitionColors(
        colorPickerProps.valueDependency
      );
      const newColor = colors?.find((c) => c?.id === colorPickerProps.colorId);
      dispatch(
        colorActions.selectColorAndTechnology({
          ...newColor,
          tech: colorPickerProps.tech,
        })
      );
    } else {
      this.currentStep.cheapestPackages = this.currentStep.cheapestPackages.map(
        (p) => {
          const findedByUpc = response.find(
            (insPackage) =>
              insPackage.lensPackage &&
              insPackage.lensPackage.upc === p.lensPackage.upc
          );
          if (findedByUpc) return findedByUpc;
          return p;
        }
      );
    }

    // update frame insPrice

    if (
      !isUndefined(response[0]?.frame.insPrice) &&
      !isNull(response[0]?.frame.insPrice) &&
      response.every(
        (r) => !isNull(r.frame.insPrice) && !isUndefined(r.frame.insPrice)
      )
    ) {
      this.dataFrame = {
        ...this.dataFrame,
        insPrice: response[0].frame.insPrice,
      };
    }

    // update current packages
    this.currentPackages = this.currentPackages?.map((p) => {
      const findedByUpc = response.find(
        (insPackage) =>
          insPackage.lensPackage &&
          insPackage.lensPackage.upc === p.lensPackage.upc
      );
      if (findedByUpc) return findedByUpc;
      return p;
    });

    // update all current packages
    this.allCurrentPackages = {
      ...this.allCurrentPackages,
      validRXPackages: this.allCurrentPackages.validRXPackages?.map((p) => {
        const findedByUpc = response.find(
          (insPackage) =>
            insPackage.lensPackage &&
            insPackage.lensPackage.upc === p.lensPackage.upc
        );
        if (findedByUpc) return findedByUpc;
        return p;
      }),
    };

    if (this.currentStep.key === 'LensColour') {
      // update prices (nav / footer)
      this.currentPrices = useCurrentPrices(response, this.config);
    } else {
      // update prices (nav / footer)
      this.currentPrices = useCurrentPrices(this.currentPackages, this.config);
    }

    if (this.currentStep.key === 'LensReview') {
      const isSpeedyRXActive = this.speedyRxModule?.isActive() && this.isSpeedyActive;
      const speedyPrices = {
        discount: isSpeedyRXActive
          ? parseFloat(this.speedyRxModule?.listPrice ?? '0') -
            parseFloat(this.speedyRxModule?.offerPrice ?? '0')
          : 0,
        price: isSpeedyRXActive
          ? parseFloat(this.speedyRxModule?.listPrice ?? '0')
          : 0,
      };

      if (isSpeedyRXActive) {
        this.currentPrices.total.price += speedyPrices.price;
        this.currentPrices.total.discount += speedyPrices.discount;
        if (this.currentPrices.total.insPrice) {
          this.currentPrices.total.insPrice += speedyPrices.price;
        }
      }
    }

    dispatch(workflowActions.setCurrentStepPrices());
  }

  getInsurancePrices(dispatch: any, currentPackages?: LensPackage[]) {
    const cp =
      currentPackages ||
      (this.currentStep.cheapestPackages.length
        ? this.currentStep.cheapestPackages
        : this.currentPackages);
    if (
      !cp.length ||
      cp.every((p) => p.lensPackage.insPrice || p.lensPackage.insPrice === 0)
    ) {
      console.warn('no packages to send to ria!');
      dispatch(workflowActions.setCurrentStepPrices());
      return;
    }

    //mock insurance
    // this.mockInsuranceResponse(this.dataFrame, cp, dispatch).then(
    //   (response) => {
    //     this.updatePackagesAndPricesFromRIA(response);

    //     // callback
    //     dispatch(insuranceActions.setInsuranceLoading({
    //   isLoading: true,
    //   stepKey: null,
    // }));
    //     dispatch(workflowActions.setCurrentStepPrices());
    //   }
    // );

    callInsuranceService(
      cp,
      this.config?.insuranceModule,
      this.dataFrame,
      dispatch,
      this.discountMethod,
      this.currentStep.key
    );
  }

  setIsWaitingForPrescription(value: boolean) {
    this.isWaitingForPrescription = value;
  }

  #filterPackages(packages?: LensPackage[]) {
    if (!Array.isArray(packages)) return [];

    let params = {};
    let pcps = this.getPrevCurrentParameterStep();
    let noPrescOpt = pcps.filter(
      (pcp: any) => pcp.value === 'Non_prescription'
    );
    if (noPrescOpt.length > 0) {
      pcps = pcps.filter((pcp: any) => pcp.key !== 'type');
      pcps.unshift(noPrescOpt[0]);
    }

    pcps.forEach((p) => {
      if (p.key === 'thickness' && p.attributes && p.attributes.designType) {
        let paramToAdd = { designType: p.attributes.designType };
        const mapped = parameterToPlainAdapter.adapt(p);
        if (mapped) params = { ...params, ...mapped };
        params = { ...params, ...mapped, ...paramToAdd };
      } else if (p.key === 'addOns') {
        let ret = new Parameter();
        Object.entries(p).forEach(([key, val]) => {
          if (key !== 'key') {
            if (key === 'value') {
              if (val) {
                ret['key'] = val.type;
                ret[key] = val.selection;
              } else {
                ret['key'] = 'addOns';
                ret[key] = null;
              }
            } else {
              ret[key] = val;
            }
          }
        });
        const mapped = parameterToPlainAdapter.adapt(ret);
        if (mapped) params = { ...params, ...mapped };
      } else if (p.key === 'color' && p.attributes) {
        //added colorCategory since colorId is not unique
        let ret = {};
        ret[p.key] = p.value;
        if (p.optionalAttributes?.key && p.optionalAttributes.key !== '') {
          ret[p.optionalAttributes.key] = p.optionalAttributes.value;
        }

        if (
          p.attributes.colorCategory &&
          p.attributes.colorCategory.toLowerCase() !== 'null'
        ) {
          ret['colorCategory'] = p.attributes.colorCategory;
        }
        params = { ...params, ...ret };
      } else if (p.key === 'type') {
        let id = p.value;
        if (['reading', 'distance'].includes(id.toLowerCase())) {
          id = packages.find((currentPackage) =>
            currentPackage.lensPackage?.type?.toLowerCase().includes('single')
          )?.lensPackage.type;
        }
        const mapped = parameterToPlainAdapter.adapt({ ...p, value: id });
        if (mapped) params = { ...params, ...mapped };
      } else if (p.key === 'gvpTreatment') {
        //Skip this param, because there is also the "treatment one", it's useful only for your selections
        return { ...params };
      } else {
        const mapped = parameterToPlainAdapter.adapt(p);
        if (mapped) params = { ...params, ...mapped };
      }
    });

    console.log('#GVP filterpackages', params);

    return packages.filter((s) =>
      filterExistingProperties(s.lensPackage, params)
    );
  }

  getCurrentPackages(includeRXFiltered: Boolean) {
    let filteredPackages = this.#filterPackages(this.initialLensPackages);

    if (this.bundlesEnabled) {
      const frameOnly = filteredPackages.find(
        (p) => p.lensPackage.type === 'Frame Only'
      );

      filteredPackages = filteredPackages
        .filter((p) => {
          if (
            this.bundles.find(
              ({ lensPackageUPC }) => p.lensPackage.upc === lensPackageUPC
            )
          ) {
            return true;
          } else {
            return false;
          }
        })
        .map((p) => {
          const bundle = this.bundles.find(
            ({ lensPackageUPC }) => p.lensPackage.upc === lensPackageUPC
          );
          const { listPrice, offerPrice } = this.bundlesPrices.find(
            ({ bundleUPC }) => bundle.bundleUPC === bundleUPC
          );
          return {
            ...p,
            lensPackage: {
              ...p.lensPackage,
              listPrice: listPrice,
              offerPrice: offerPrice,
              originalListPrice: p.lensPackage.listPrice,
              originalOfferPrice: p.lensPackage.offerPrice,
              lensBundle: bundle.bundleName,
            },
          };
        });
      if (frameOnly) filteredPackages.push(frameOnly);

      filteredPackages = this.#filterPackages(filteredPackages);
    }

    // if (currentStep && promoType && filterPromoAtStep) {
    //   const filterGap = workflow.findByStepKey(filterPromoAtStep);
    //   if (filterGap && filterGap.progressive <= currentStep.progressive)
    //     filtered = filtered.filter((f) => f.frame.promoType === promoType);
    // }
    //clean treatments selected when navigation is before treatments step
    // if (
    //   this.currentStep &&
    //   this.currentStep.progressive <=
    //     this.findByStepKey('GVPTreatment')?.progressive
    // ) {
    //   this.gvpSelectionMade = false;
    // }
    if (
      this.currentStep &&
      (this.currentStep.progressive <=
        this.findByStepKey('LensTreatment')?.progressive ||
        this.currentStep.progressive <=
          this.findByStepKey('GVPTreatment')?.progressive)
    ) {
      this.currentTreatments = null;
    }

    if (!includeRXFiltered) {
      // se siamo nel primo step non c'è bisogno di fare il controllo sulla prescrizione
      const prescriptionStep = this.findByStepKey('LensPrescription');
      const isNonPrescriptionOrFrameOnly = prescriptionStep?.params?.find(
        (p) =>
          p.key === 'type' &&
          (p.value === 'Non_prescription' || p.value === 'Frame Only')
      );
      if (
        this.currentPrescription &&
        prescriptionStep &&
        this.currentStep?.progressive >= prescriptionStep?.progressive &&
        !isNonPrescriptionOrFrameOnly
      ) {
        filteredPackages = filteredPackages.filter((f) =>
          hasValidPrescription(f, this.currentPrescription)
        );
      }
      this.currentPackages = filteredPackages;
    } else {
      let validRXPackages = [];
      let invalidRXPackages = [];

      // recupero se ho inserito un tipo frame only o non prescription
      const prescriptionStep = this.findByStepKey('LensPrescription');
      const isNonPrescriptionOrFrameOnly = prescriptionStep?.params?.find(
        (p) =>
          p.key === 'type' &&
          (p.value === 'Non_prescription' || p.value === 'Frame Only')
      );

      filteredPackages?.forEach((pkg) => {
        // se siamo nel primo step non c'è bisogno di fare il controllo sulla prescrizione

        if (
          this.currentPrescription &&
          prescriptionStep &&
          this.currentStep?.progressive >= prescriptionStep?.progressive &&
          !isNonPrescriptionOrFrameOnly
        ) {
          /* se ho passato la prescrizione e non inserito frame only o 
          non prescription filtro i pacchetti con la prescrizione */
          if (hasValidPrescription(pkg, this.currentPrescription)) {
            validRXPackages.push(pkg);
          } else {
            invalidRXPackages.push(pkg);
          }
        } else {
          validRXPackages.push(pkg);
        }
      });
      // if (this.gvpSelectionMade) {
      //   validRXPackages = validRXPackages.filter(
      //     (pkg: any) => pkg?.frame?.promoType?.toLowerCase() === 'gvp'
      //   );
      // }
      this.allCurrentPackages = {
        validRXPackages: validRXPackages,
        invalidRXPackages: this.enableGrayout ? invalidRXPackages : [],
      };
    }
  }

  // filter packages by attribute (only inside a step)

  filterPackagesByAttribute(
    params: { [key: string]: any },
    packages?: LensPackage[]
  ) {
    let parameters = {};
    Object.keys(params).forEach((key) => {
      if (
        key === 'type' &&
        ['reading', 'distance'].includes(params[key].id.toLowerCase())
      ) {
        return (parameters[key] = 'SingleVision');
      }
      return (parameters[key] = params[key]?.id);
    });
    return (packages || this.currentPackages)?.filter((s) =>
      filterExistingProperties(s.lensPackage, parameters)
    );
  }

  filterStepPackages(additionalParameters?: any) {
    let params = {};
    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        if (val) {
          parametersToAdd.push(...this.getParameters(key, val));
        }
      });
      parametersToAdd.forEach((p) => {
        if (p.key === 'type') {
          let id = p.value;
          if (['reading', 'distance'].includes(id.toLowerCase())) {
            id = this.currentPackages.find((currentPackage) =>
              currentPackage.lensPackage?.type?.toLowerCase().includes('single')
            )?.lensPackage.type;
          }
          const mapped = parameterToPlainAdapter.adapt({ ...p, value: id });
          if (mapped) params = { ...params, ...mapped };
        } else {
          const mapped = parameterToPlainAdapter.adapt(p);
          params = { ...params, ...mapped };
        }
      });
    }

    const filteredPackages = this.currentPackages.filter((s) =>
      filterExistingProperties(s.lensPackage, params)
    );
    let validRXPackages = [];
    let invalidRXPackages = [];

    // recupero se ho inserito un tipo frame only o non prescription
    const prescriptionStep = this.findByStepKey('LensPrescription');
    const isNonPrescriptionOrFrameOnly = prescriptionStep?.params?.find(
      (p) =>
        p.key === 'type' &&
        (p.value === 'Non_prescription' || p.value === 'Frame Only')
    );

    filteredPackages?.forEach((pkg) => {
      /* se siamo nel primo step non c'è bisogno di fare il controllo sulla prescrizione 
      inoltre se non passiamo proprio lo step di prescrizione non dobbiamo filtrare */

      if (
        this.currentPrescription &&
        prescriptionStep &&
        this.currentStep?.progressive >= prescriptionStep?.progressive &&
        !isNonPrescriptionOrFrameOnly
      ) {
        /* se ho passato la prescrizione e non inserito frame only o 
        non prescription filtro i pacchetti con la prescrizione */
        if (hasValidPrescription(pkg, this.currentPrescription)) {
          validRXPackages.push(pkg);
        } else {
          invalidRXPackages.push(pkg);
        }
      } else {
        validRXPackages.push(pkg);
      }
    });
    // if (this.gvpSelectionMade) {
    //   validRXPackages = validRXPackages.filter(
    //     (pkg: any) => pkg?.frame?.promoType?.toLowerCase() === 'gvp'
    //   );
    // }
    return {
      validRXPackages: validRXPackages,
      invalidRXPackages: invalidRXPackages,
    };
  }

  getParameters(attribute: string, option: any) {
    switch (attribute.toLocaleLowerCase()) {
      case 'type':
        return this.getTypeParameters(option);
      case 'brand':
        return this.getBrandParameters(option);
      case 'clear':
        return this.getClearParameters(option);
      case 'bluelight':
        return this.getBlueLightParameters(option);
      case 'transition':
        return this.getTransitionParameters(option);
      case 'sun':
        return this.getSunParameters(option);
      case 'gvptreatment':
        return this.getGVPTreatmentsParameters(option);
      case 'treatment':
        return this.getTreatmentsParameters(option);
      case 'warrantyoptions':
        return this.getWarrantyParameters(option);
      case 'thickness':
        return this.getThicknessParameters(option);
      // case "addons":
      //   return this.#getAddonsOptions();
      case 'antireflective':
        return this.getAntiReflectiveParameters(option, true);
      case 'colorcategory':
        return this.getColorCategoriesParameters(option);
      case 'polar':
        return this.getPolarParameters(option);
      case 'designtype':
        return this.getDesignTypeParameters(option);
      default:
        return this.getGenericParameters(attribute, option);
    }
  }

  // gets all the options for each attribute
  getOptions({
    attribute,
    labels,
    autoSkip,
    dispatch,
    parentSelections,
  }: {
    attribute: string;
    labels?: { [key: string]: string };
    autoSkip?: boolean;
    dispatch?: any;
    parentSelections?: any;
  }): GetOptions {
    switch (attribute?.toLocaleLowerCase()) {
      case 'type':
        return this.#getTypeOptions(parentSelections, dispatch);
      case 'brand':
        return this.#getBrandOptions(autoSkip, parentSelections);
      case 'lensbundle':
        return this.#getBundlesOptions(autoSkip, parentSelections);
      case 'clear':
        return this.#getClearOptions(labels, dispatch, parentSelections);
      case 'bluelight':
        return this.#getBlueLightOptions(labels, dispatch, parentSelections);
      case 'bluelightopt':
        return this.#getBlueLightSpecificOptions(
          dispatch,
          parentSelections,
          autoSkip
        );
      case 'transition':
        return this.#getTransitionOptions(labels, parentSelections);
      case 'transitionopt':
        return this.#getTransitionSpecificOptions(labels, parentSelections);
      case 'sun':
        return this.#getSunOptions(labels, dispatch, parentSelections);
      case 'gvptreatment':
        return this.#getGVPTreatmentsOptions(
          dispatch,
          parentSelections,
          autoSkip
        );
      case 'treatment':
        return this.getTreatments(dispatch, parentSelections, autoSkip);
      case 'warrantyoptions':
        return this.#getWarrantyOptions(labels);
      case 'thickness':
        return this.#getThicknessOptions(parentSelections);
      case 'addons':
        return this.#getAddonsOptions(labels, parentSelections);
      case 'colorcategory':
        return this.#getColorCategories(parentSelections);
      case 'polar':
        return this.#getPolarOptions(parentSelections);
      case 'designtype':
        return this.#getDesignTypeOptions(parentSelections);
      default:
        return this.#getGenericOptions(attribute, parentSelections);
    }
  }
  // bundles

  #getBundlesOptions(autoSkip: boolean, parentSelections?: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const packages: LensPackage[] =
      filteredPackages?.validRXPackages || this.currentPackages;
    // viene fatto direttamente il filtro sul prezzo poiché se presente allora è tra i bundle validi
    const bundles = getBundles(this.config, packages).filter((b) => b.price);

    const onBundleSelected = (bundle: any, skipped?: boolean) => {
      this.goNextStep(
        bundle?.id
          ? [
              ...(skipped ? this.currentStep.params : []),
              {
                key: 'lensBundle',
                value: bundle.id,
                price: bundle.price,
                discount: bundle.discount,
                insPrice: skipped && bundle.price == 0 ? 0 : bundle.insPrice,
                attributes: {
                  showSeeDetails: true,
                },
              },
            ]
          : [],
        skipped
      );
    };
    return [bundles, onBundleSelected];
  }

  // get transition colors
  getTransitionColors(id?: string, parentSelections?: any): GetOptions {
    if (id) {
      return this.#getTransitionColors(id);
    } else return [[], () => {}];
  }

  // get colors (sun)
  getSunColorsParameters(
    color: any,
    skipped?: boolean,
    additionalParameters?: any,
    transitionParam?: any
  ) {
    const parameters = [
      {
        key: 'color',
        value: color?.id,
        price: color.price,
        discount: color.discount,
        insPrice: skipped && color.price == 0 ? 0 : color.insPrice,
        attributes: {
          translatedName: color.name,
          colorCategory: color.colorCategory,
        },
        optionalAttributes: {
          key: color?.brand ? 'brand' : '',
          value: color?.brand,
        },
      },
      // {
      //   key: "color",
      //   value: color?.name ? color?.name : color?.id,
      //   price: color.price,
      //   discount: color.discount,
      //   insPrice: skipped && color.price == 0 ? 0 : color.insPrice,
      //   attributes: {
      //     translatedName: color.name,
      //     colorCategory: color.colorCategory,
      //   },
      //   optionalAttributes: {
      //     key: color?.brand ? "brand" : "",
      //     value: color?.brand,
      //   },
      // },
    ];
    if (additionalParameters) {
      const parametersToAdd = [];
      if (
        Object?.keys(additionalParameters)?.length === 1 &&
        additionalParameters['GVPtreatment']
      ) {
        let gvpOpt = this.#getGVPTreatmentsOptions(null, null, false);
        let correctGVP = gvpOpt?.[0]?.filter(
          (opt: any) => opt?.id === additionalParameters['GVPtreatment']?.id
        );
        let gvpParam = this.getGVPTreatmentsParameters(correctGVP?.[0]);
        parameters.push(...gvpParam);
      } else if (
        Object?.keys(additionalParameters)?.length === 1 &&
        additionalParameters['treatmentsFamily']
      ) {
        let transOpt = this.#getTransitionOptions({}, null);
        let correctTrans = transOpt?.[0]?.filter(
          (opt: any) => opt?.id === additionalParameters['treatmentsFamily']?.id
        );
        let transParam = this.getGVPTreatmentsParameters(correctTrans?.[0]);
        parameters.push(...transParam);
      } else {
        // const additionalOption =
        Object.entries(additionalParameters).forEach(([key, val]) => {
          parametersToAdd.push(...this.getParameters(key, val));
        });
        parameters.push(...parametersToAdd);
      }
    }
    if (transitionParam) {
      if (Array.isArray(transitionParam)) {
        parameters.push(...transitionParam);
      } else {
        parameters.push(transitionParam);
      }
    }
    return parameters;
  }

  getSunColors(parentSelections?: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const colors = getSunColors({
      currentPackages: filteredPackages || this.allCurrentPackages,
      content: this.content,
    });

    const handleSelected = (
      color: any,
      skipped?: boolean,
      parentSelections?: any,
      transitionParam?: any
    ) => {
      this.goNextStep(
        this.getSunColorsParameters(
          color,
          skipped,
          parentSelections,
          transitionParam
        ),
        skipped
      );
    };

    // if (colors.length === 1) {
    //   if (!this.currentStep?.params?.find((p) => p.key === "color")) {
    //     this.goToStep(
    //       "LensMaterial",
    //       this.getSunColorsParameters(colors[0], true),
    //       true
    //     );
    //   }
    // }

    // from cart
    // if (this.packageFromCart && this.enablePreselect) {
    //   const color = colors?.find(
    //     (c) => c?.id === this.packageFromCart?.lensPackage.color
    //   );
    //   if (color) {
    //     let pcps = this.getPrevCurrentParameterStep();
    //     if (pcps.find((p) => p.key === "color")) {
    //       this.goNextStep([], true);
    //     } else {
    //       handleSelected(color, false);
    //     }
    //   }
    // }

    return [colors, handleSelected];
  }

  // get color categories

  getColorCategoriesParameters(option: any) {
    return [
      {
        key: 'colorCategory',
        value: option.id,
      },
    ];
  }

  #getColorCategories(parentSelections?: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const categories = getColorCategories(
      filteredPackages || this.allCurrentPackages,
      this.content
    );
    return [categories, () => {}];
  }
  // get polar options
  getPolarParameters(option: any) {
    return [
      {
        key: 'polar',
        value: option.id,
      },
    ];
  }

  #getPolarOptions(parentSelections?: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const allPolarValues = (
      filteredPackages?.validRXPackages || this.currentPackages
    )
      ?.reduce((values, pkg) => {
        if (!values.includes(pkg.lensPackage.polar)) {
          values.push(pkg.lensPackage.polar);
        }
        return values;
      }, [])
      .map((value) => ({ id: value }));
    return [allPolarValues, () => {}];
  }

  // get type options (single vision, progressive ecc...)
  getTypeParameters(option: any, additionalParameters?: any) {
    let parameters: Parameter[] = [];
    if (option.id === 'Prescription') {
      // option.values.forEach((v) =>
      //   parameters.push({
      //     key: 'type',
      //     value: v.id,
      //   })
      // );
    } else {
      parameters = [
        {
          key: 'type',
          value: option.id,
        },
      ];
    }

    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }
    return parameters;
  }
  #getTypeOptions(parentSelections?: any, dispatch: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const types = getTypes(
      this.config,
      this.initialLensPackages,
      filteredPackages?.validRXPackages || this.currentPackages,
      this.currentPrescription,
      this.config.prescriptionModule?.activateDistanceAndReading ||
        this.flowDefiningAttributes?.activateDistanceAndReading,
      parentSelections
    );

    const onTypeSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      this.lensType = option.id;
      let parameters = this.getTypeParameters(option, additionalParameters);

      if (
        option.id ===
        getFrameOnlyKey(this.initialLensPackages, this.config.data)
      ) {
        dispatch(prescriptionActions.setCurrentPrescription(null));
        this.goToStep('LensReview', parameters, skipped);
      }

      const isDistanceAndReading = ['reading', 'distance'].includes(
        option.id?.toLowerCase()
      );

      if (isDistanceAndReading)
        parameters[0].optionalAttributes = {
          key: 'reading&distance',
          value: option.id,
        };

      this.goNextStep(parameters, skipped);
    };

    // from cart
    // if (this.packageFromCart && this.enablePreselect) {
    //   const preselectedOption = this.packageFromCart.lensPackage["type"];
    //   if (
    //     (this.currentPrescription?.ADD_OD ||
    //       this.currentPrescription?.ADD_OS) &&
    //     preselectedOption.toLowerCase() === "single vision"
    //   ) {
    //     onTypeSelected({ id: "READING" }, false);
    //   } else {
    //     onTypeSelected({ id: preselectedOption }, false);
    //   }
    // }

    return [types, onTypeSelected];
  }
  // get warranty options (EPP)
  getWarrantyParameters(optionSelected?: any) {
    return [
      {
        key: 'protectionplan',
        value: optionSelected?.title,
        price: optionSelected?.price,
        discount: optionSelected?.discount || 0,
        insPrice: optionSelected?.price,
      },
    ];
  }
  setSelectedReviewWarranty(warranty: any) {
    this.selectedReviewWarranty = warranty;
  }

  #getWarrantyOptions(labels?: { [key: string]: string }): GetOptions {
    const noProtection = {
      id: 'asIs',
      title: labels?.protectionPlanAsIs,
      price: null,
      discount: 0,
      insPrice: 0,
    };
    // to do
    // selection from type step (value)
    const currentLensType = this.lensType;

    const filteredWarrantyOptions =
      this.warrantyOptions?.length > 0 &&
      this.warrantyOptions.filter((wo: any) =>
        wo?.type?.toLowerCase().includes(currentLensType?.toLowerCase())
      );
    const firstWarrantyOption = this.warrantyOptions?.[0];

    let fullWarrantyOptions = [noProtection];

    if (filteredWarrantyOptions?.length > 0) {
      fullWarrantyOptions = [
        ...filteredWarrantyOptions,
        ...fullWarrantyOptions,
      ].map((option) => {
        return {
          ...option,
          discount: 0,
        };
      });
    } else {
      if (firstWarrantyOption) {
        fullWarrantyOptions.unshift(firstWarrantyOption);
      }
    }

    const onProtectionSelected = (option: any) => {
      const optionSelected = fullWarrantyOptions.find(
        (wo: any) => wo.id === option.id
      );

      this.selectedWarranty = optionSelected;

      const parameters = this.getWarrantyParameters(optionSelected);

      this.goNextStep(
        parameters,
        fullWarrantyOptions?.length > 1 ? false : true
      );
    };

    // from cart
    // if (this.packageFromCart && this.enablePreselect) {
    //   const preselectedOption = this.config.data?.warranty?.catEntryId;
    //   if (preselectedOption) {
    //     onProtectionSelected({ id: preselectedOption });
    //   } else {
    //     onProtectionSelected("asIs");
    //   }
    // }

    return [fullWarrantyOptions, onProtectionSelected];
  }
  // get brand options
  getBrandParameters(
    option: string,
    skipped?: boolean,
    additionalParameters?: any
  ) {
    const priceObj = getPriceStep(
      { brand: typeof option === 'string' ? option : option?.id },
      this.currentPackages
    );
    const parameters = [
      {
        key: 'brand',
        value: typeof option === 'string' ? option : option?.id,
        price: skipped ? 0 : priceObj?.price,
        discount: priceObj?.discount,
        insPrice: skipped && priceObj?.price == 0 ? 0 : priceObj?.insPrice,
      },
    ];

    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }
    return parameters;
  }
  #getBrandOptions(autoSkip?: boolean, parentSelections?: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const brandsList = getBrands(
      this.content,
      filteredPackages || this.allCurrentPackages
    );

    const onBrandSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      const parameters = this.getBrandParameters(
        option,
        skipped,
        additionalParameters
      );
      console.log(this.currentStep);
      // if (this.currentStep?.key === "LensBrand") {
      this.goNextStep(parameters, skipped);
      // }
    };

    // if (brandsList?.length === 1 && autoSkip && !this.packageFromCart) {
    //   if (!this.currentStep?.params?.find((p) => p.key === "brand")) {
    //     onBrandSelected(brandsList[0], true);
    //     showStepSkeleton();
    //   }
    // }

    // from cart
    // if (this.packageFromCart && this.enablePreselect) {
    //   const brand = this.packageFromCart.lensPackage.brand;
    //   if (brand) {
    //     onBrandSelected({ id: brand }, false);
    //   }
    // }
    return [brandsList, onBrandSelected];
  }
  //get generic options
  getGenericParameters(
    field: string,
    option: any,
    skipped?: boolean,
    additionalParameters?: any
  ) {
    const filterP: any = { [field]: option.id };
    // else if (designTypeSelected) {
    //   filterP.designType = designTypeSelected;
    // }

    const priceObj = getPriceStep(
      filterP,
      this.allCurrentPackages?.validRXPackages
    );
    const parameters = [
      {
        key: field,
        value: option.id,
        price: priceObj?.price,
        discount: priceObj?.discount,
        insPrice: skipped && priceObj?.price == 0 ? 0 : priceObj?.insPrice,
      },
    ];

    if (skipped) {
      parameters.push(...this.currentStep.params);
    }

    // if (additionalParameters) {
    //   const parametersToAdd = [];
    //   Object.entries(additionalParameters).forEach(([key, val]) => {
    //     parametersToAdd.push(...this.getParameters(key, val));
    //   });
    //   parameters.push(...parametersToAdd);
    // }

    return parameters;
  }
  #getGenericOptions(field: string, parentSelections?: any): GetOptions {
    console.log('####getGenericOptions, ', parentSelections);
    const filteredPackages = this.filterStepPackages(parentSelections);
    const genericOptions = getOptionsByType(
      field,
      this.content,
      filteredPackages ?? this.allCurrentPackages
    );
    const onSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      if (!option?.id) {
        this.goNextStep([]);
        //TODO
        //console.log(no value)
        return;
      }

      const parameters = this.getGenericParameters(
        field,
        option,
        skipped,
        additionalParameters
      );
      this.goNextStep(parameters, skipped);
    };
    return [genericOptions, onSelected];
  }
  // get clear options
  getClearParameters(
    option: any,
    skipped?: boolean,
    additionalParameters?: any
  ) {
    let dontSendPrice = option?.thereAreNonIncludedOptions;
    const parameters = [
      {
        key: 'blueLight',
        value: null,
        price: dontSendPrice ? 0 : option.price,
        discount: dontSendPrice ? 0 : option.discount,
        insPrice: dontSendPrice
          ? 0
          : skipped && option.price == 0
            ? 0
            : option.insPrice,
      },
      {
        key: 'transition',
        value: null,
        price: dontSendPrice ? 0 : option.price,
        discount: dontSendPrice ? 0 : option.discount,
        insPrice: dontSendPrice
          ? 0
          : skipped && option.price == 0
            ? 0
            : option.insPrice,
      },
      {
        key: 'familyFilter',
        value: {
          required: [],
          excluded: ['blueLight', 'transition', 'color'],
          additional: [],
          nextStep: 'Thickness',
        },
        price: option.price,
        discount: option.discount,
        insPrice: option.insPrice,
        attributes: {
          hideTreatmentsInReview: true,
          skipped: skipped,
        },
      },
      {
        key: 'treatmentsFamily',
        value: option.title,
        price: dontSendPrice || !option.price ? 0 : option.price,
        discount: dontSendPrice || !option.discount ? 0 : option.discount,
        insPrice:
          dontSendPrice || !option.insPrice
            ? 0
            : skipped && option.price == 0
              ? 0
              : option.insPrice,
      },
    ];
    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }
    return parameters;
  }
  #getClearOptions(
    labels?: { [key: string]: string },
    dispatch?: any,
    parentSelections?: any
  ): GetOptions {
    // reset transition preview image
    dispatch?.(colorActions.selectTransition(''));

    const filteredPackages = this.filterStepPackages(parentSelections);
    const clearOptions = getTreatmentsFamily(
      this.content,
      filteredPackages || this.allCurrentPackages,
      'clear',
      labels
    );
    const onTreatmentsSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      const id = option.id;
      let selectedTfamilyOptions = clearOptions.filter((opt: any) => {
        return opt.id === id;
      });

      dispatch?.(colorActions.setClearClen('CLEAR'));
      dispatch?.(colorActions.selectTransition(''));
      this.goNextStep(
        this.getClearParameters(
          selectedTfamilyOptions[0],
          skipped,
          additionalParameters
        ),
        skipped
      );
    };

    // from cart
    // if (this.packageFromCart && this.enablePreselect) {
    //   const isClear = cleanRule(this.packageFromCart.lensPackage);
    //   if (isClear) {
    //     onTreatmentsSelected(clearOptions[0], false);
    //   }
    // }
    return [clearOptions, onTreatmentsSelected];
  }
  // get blueLight options
  getBlueLightParameters(
    option: any,
    skipped?: boolean,
    additionalParameters?: any
  ) {
    let parameters = [];
    if (option.id.indexOf('$') >= 0) {
      const idT = option.id.split('$')[0];
      const valueT = option.id.split('$')[1];

      //to reload price
      const onlySelected = new ParameterWithTitle();
      const idTa = idT;
      const valueTa = valueT;
      onlySelected.key = idTa;
      onlySelected.value = valueTa;
      onlySelected.title = option.title;
      onlySelected.price = option.price;
      onlySelected.discount = option.discount;
      onlySelected.insPrice = option.insPrice;
      parameters = [
        {
          key: 'familyFilter',
          value: {
            required: ['blueLight'],
            excluded: ['transition', 'color'],
            additional: [],
            nextStep: 'Thickness',
          } as any,
        },
        {
          key: 'treatmentsFamily',
          value: option.title,
          price: option.price,
          discount: option.discount,
          insPrice: option.insPrice,
          attributes: {
            hideTreatmentsInReview: true,
            skipped: skipped,
          },
        },
        onlySelected,
      ];
    } else {
      let dontSendPrice = option.thereAreNonIncludedOptions;
      parameters = [
        {
          key: 'familyFilter',
          value: {
            required: ['blueLight'],
            excluded: ['transition'],
            additional: [],
            nextStep: 'Thickness',
          } as any,
        },
        {
          key: 'treatmentsFamily',
          value: option.title,
          price: dontSendPrice ? 0 : option.price,
          discount: dontSendPrice ? 0 : option.discount,
          insPrice: dontSendPrice
            ? 0
            : skipped && option.price == 0
              ? 0
              : option.insPrice,
        },
      ];
    }
    // if (additionalParameters) {
    //   const parametersToAdd = [];
    //   Object.entries(additionalParameters).forEach(([key, val]) => {
    //     parametersToAdd.push(...this.getParameters(key, val));
    //   });
    //   parameters.push(...parametersToAdd);
    // }
    return parameters;
  }
  #getBlueLightOptions(
    labels?: { [key: string]: string },
    dispatch?: any,
    parentSelections?: any
  ): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const blueLightOptions = getTreatmentsFamily(
      this.content,
      filteredPackages || this.allCurrentPackages,
      'blueLight',
      labels
    );

    const onTreatmentsSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      const id = option.id;
      let selectedTfamilyOptions = blueLightOptions.filter((opt: any) => {
        return opt.id === id;
      });
      dispatch?.(colorActions.setClearClen('CLEAR'));
      dispatch?.(colorActions.selectTransition(''));
      this.goNextStep(
        this.getBlueLightParameters(
          selectedTfamilyOptions[0],
          skipped,
          additionalParameters
        ),
        skipped
      );
    };
    return [blueLightOptions, onTreatmentsSelected];
  }
  #getBlueLightSpecificOptions(
    dispatch?: any,
    parentSelections?: any,
    autoSkip?: any
  ) {
    const filteredPackages = this.filterStepPackages(parentSelections);
    let { treatments } = loadTreatments(
      this.content,
      filteredPackages || this.allCurrentPackages,
      false,
      ['blueLight']
    );
    const onClickTreatment = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      if (option.id) {
        const parameters = this.getTreatmentsParameters(option);
        this.goNextStep(parameters, skipped);
      } else {
        // this.goNextStep([], skipped);
      }
    };

    return [treatments, onClickTreatment];
  }
  // get transition options
  getTransitionParameters(
    option: any,
    skipped?: boolean,
    additionalParameters?: any
  ) {
    const familyFilter = getFamilyFilter(this.currentStep);
    const parameters = [
      {
        key: 'familyFilter',
        value: {
          required: ['transition'],
          additional: [],
          excluded: [],
          // nextStep: "TransitionColor",
        } as any,
      },
    ];
    if (option?.id.indexOf('$') >= 0) {
      const treatment = this.getTreatments(null, option);
      const tOptions = treatment[0]?.[0];

      parameters.push({
        price: tOptions && tOptions.price,
        insPrice: tOptions && tOptions.insPrice,
        discount: tOptions && tOptions.discount,
        key: 'treatmentsFamily',
        value:
          this.config.lensesData.content?.['treatment']?.[
            option.id.split('$')[1]
          ]?.title,
      });
    }

    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }

    return parameters;
  }
  #getTransitionOptions(
    labels?: { [key: string]: string },
    parentSelections?: any
  ): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const transitionPackages = {
      invalidRXPackages: [...filteredPackages?.invalidRXPackages],
      validRXPackages: [
        ...filteredPackages?.validRXPackages?.filter(
          (pkg: any) => pkg?.lensPackage?.transition
        ),
      ],
    };
    const transitionOptions = getTreatmentsFamily(
      this.content,
      filteredPackages || this.allCurrentPackages,
      'transition',
      labels
    );
    let { treatments } = loadTreatments(
      this.content,
      transitionPackages || this.allCurrentPackages,
      false,
      ['transition']
    );
    const onTreatmentsSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      const parameters = this.getTransitionParameters(
        option,
        skipped,
        additionalParameters
      );
      this.goNextStep(parameters, skipped);
    };
    return [
      this?.flowDefiningAttributes?.notShowTreatmentFamily
        ? treatments
        : transitionOptions,
      onTreatmentsSelected,
    ];
  }
  #getTransitionSpecificOptions(
    dispatch?: any,
    parentSelections?: any,
    autoSkip?: any
  ) {
    const filteredPackages = this.filterStepPackages(parentSelections);
    let { treatments } = loadTreatments(
      this.content,
      filteredPackages || this.allCurrentPackages,
      false,
      ['transition']
    );
    const onClickTreatment = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      if (option.id) {
        const parameters = this.getTreatmentsParameters(option);
        this.goNextStep(parameters, skipped);
      } else {
        // this.goNextStep([], skipped);
      }
    };

    return [treatments, onClickTreatment];
  }
  // get sun options
  getSunParameters(option: any, skipped?: boolean, additionalParameters?: any) {
    let dontSendPrice = option.thereAreNonIncludedOptions;
    const parameters = [
      {
        key: 'treatmentsFamily',
        value: option.title || option.id,
        price: dontSendPrice ? 0 : option.price,
        discount: dontSendPrice ? 0 : option.discount,
        insPrice: dontSendPrice
          ? 0
          : skipped && option.price == 0
            ? 0
            : option.insPrice,
      },
      {
        key: 'familyFilter',
        value: {
          required: ['color'],
          additional: [],
          excluded: ['transition'],
        } as any,
      },
    ];
    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }
    return parameters;
  }
  #getSunOptions(
    labels?: { [key: string]: string },
    dispatch?: any,
    parentSelections?: any
  ): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const sunOptions = getTreatmentsFamily(
      this.content,
      filteredPackages || this.allCurrentPackages,
      'sun',
      labels
    );
    const onTreatmentsSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      const id = option.id;
      let selectedTfamilyOptions = sunOptions.filter((opt: any) => {
        return opt.id === id;
      });

      dispatch?.(colorActions.setClearClen(null));
      dispatch?.(colorActions.selectTransition(''));

      this.goNextStep(
        this.getSunParameters(
          selectedTfamilyOptions[0],
          skipped,
          additionalParameters
        ),
        skipped
      );
    };
    return [sunOptions, onTreatmentsSelected];
  }
  // get GVP treatmentws
  getGVPTreatmentsParameters(option: any, additionalParameters?: any) {
    console.log('#Gvp option', option);
    const parameters = [
      {
        key: 'GVPtreatment',
        value: option.id,
        title: option.title,
        price: option.price,
        discount: option.discount,
        insPrice: option.insPrice,
      },
      {
        key: 'treatment',
        value: option.id,
        title: option.title,
        price: option.price,
        discount: option.discount,
        insPrice: option.insPrice,
      },
    ];
    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }

    return parameters;
  }
  // get treatments
  getTreatmentsParameters(option: any, additionalParameters?: any) {
    const familyFilter = getFamilyFilter(this.currentStep);
    const id = option.id.split('$')[0];
    const value = option.id.split('$')[1];
    const parameters = [
      {
        key: id,
        value: value,
        title: option.title,
        price: option.price,
        discount: option.discount,
        insPrice: option.insPrice,
      },
      // {
      //   key: 'treatments',
      //   value: option.title,
      //   title: option.title,
      //   price: option.price,
      //   discount: option.discount,
      //   insPrice: option.insPrice,
      //   originalStep: 'Treatments',
      // },
      // {
      //   key: 'photocromic',
      //   value: isPhotocromic({
      //     content: this.content,
      //     allCurrentPackages: this.allCurrentPackages,
      //     familyFilter: familyFilter,
      //     fromTreatments: true,
      //   }),
      // },
      // {
      //   key: "treatmentsFamily",
      //   value: option.title,
      //   price: option.price,
      //   discount: option.discount,
      //   insPrice: option.insPrice,
      //   attributes: {
      //     hideTreatmentsInReview: true,
      //     // skipped: skipped,
      //   },
      // },
      // {
      //   key: "familyFilter",
      //   value: {
      //     required: ["transition"],
      //     additional: [],
      //     excluded: [],
      //     nextStep: "TransitionColor",
      //   } as any,
      // },
    ];
    return parameters;
  }
  #getGVPTreatmentsOptions(
    dispatch: any,
    parentSelections?: any,
    autoSkip?: boolean
  ): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    // let { treatments } = getTreatments(
    //   this.content,
    //   this.currentStep,
    //   filteredPackages || this.allCurrentPackages,
    //   false,
    //   dispatch
    // );

    const options = getGVPTreatments({
      content: this.content,
      currentPackages: filteredPackages || this.allCurrentPackages,
      // fromTreatments: true,
      // dispatch: dispatch,
    });
    console.log('#GVP loadTreatments', options);
    const onClickGVPTreatment = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      this.currentTreatments = option;
      // this.gvpSelectionMade = true;
      this.goNextStep(
        this.getGVPTreatmentsParameters(option, additionalParameters),
        skipped
      );
    };

    return [options, onClickGVPTreatment];
  }

  getTreatments(
    dispatch: any,
    parentSelections?: any,
    autoSkip?: boolean
  ): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    let { treatments } = loadTreatments(
      this.content,
      filteredPackages || this.allCurrentPackages,
      false,
      ['transition']
    );
    // const treatments = getTreatments({
    //   content: this.content,
    //   currentStep: this.currentStep,
    //   allCurrentPackages: filteredPackages || this.allCurrentPackages,
    //   fromTreatments: true,
    //   dispatch: dispatch,
    // });

    const onClickTreatment = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any
    ) => {
      if (option.id) {
        const parameters = this.getTreatmentsParameters(option);
        this.goNextStep(parameters, skipped);
      } else {
        this.goNextStep([], skipped);
      }
    };

    // isPhotocromic({
    //   content: this.content,
    //   allCurrentPackages: this.allCurrentPackages,
    //   familyFilter: this?.flowDefiningAttributes?.notShowTreatmentFamily
    //     ? []
    //     : ['transition'],
    //   fromTreatments: true,
    //   dispatch,
    // });

    // if (
    //   !treatments.length &&
    //   !this.currentStep?.params?.find((p) => p.key === "treatments") &&
    //   autoSkip
    // ) {
    //   this.goNextStep([], true);
    // }

    // if (
    //   treatments.length === 1 &&
    //   this.currentStep?.params?.find((p) => p.key === "color")
    // ) {
    //   onClickTreatment(treatments[0], true);
    // }

    // from cart
    // if (this.packageFromCart && this.enablePreselect) {
    //   const treatmentId = this.packageFromCart.lensPackage.transition;
    //   const selectedTreatment = treatments?.find(
    //     (treatment) => treatment.id === `transition$${treatmentId}`
    //   );
    //   const [colors] = this.getTransitionColors(
    //     `transition$${treatmentId}` || undefined
    //   );
    //   const color = colors?.find(
    //     (c) => c?.id === this.packageFromCart?.lensPackage.color
    //   );
    //   if (treatmentId) {
    //     onClickTreatment(selectedTreatment, false);
    //   }
    // }

    return [treatments, onClickTreatment];
  }

  // get transition colors
  #getTransitionColors(transitionId?: string): GetOptions {
    const colors = getTransitionColors({
      configProjectImage:
        this.flowDefiningAttributes.projectImage ||
        this.config?.baseURLs?.projectImage,
      content: this.content,
      allCurrentPackages: this.allCurrentPackages,
      id: transitionId,
    });
    const onTransitionColorSelected = (
      color: any,
      additionalParameters?: any,
      skipped?: boolean
    ) => {
      const parameters = [
        {
          key: 'color',
          value: color.id,
        },
      ];

      if (additionalParameters) {
        const parametersToAdd = [];
        Object.entries(additionalParameters).forEach(([key, val]) => {
          parametersToAdd.push(...this.getParameters(key, val));
        });
        parameters.push(...parametersToAdd);
      }

      this.goNextStep(parameters, false);
    };
    // if (this.packageFromCart && this.enablePreselect) {
    //   let color = this.packageFromCart.lensPackage.color;
    //   let transitionOptions = this.#getTransitionOptions()?.[0]?.find(
    //     (o: any) => o.id === this.packageFromCart?.lensPackage.transition
    //   );
    //   onTransitionColorSelected(color, transitionOptions);
    // }
    return [colors, onTransitionColorSelected];
  }

  // get thickness options
  getThicknessParameters(
    option: any,
    skipped?: boolean,
    additionalParameters?: any,
    parentSelection?: any
  ) {
    const filterP: any = { thickness: option.id };
    // else if (designTypeSelected) {
    //   filterP.designType = designTypeSelected;
    // }

    const priceObj = getPriceStep(
      filterP,
      this.allCurrentPackages?.validRXPackages
    );
    const parameters = [
      {
        key: 'thickness',
        value: option.id,
        price: priceObj?.price,
        discount: priceObj?.discount,
        insPrice: skipped && priceObj?.price == 0 ? 0 : priceObj?.insPrice,
      },
    ];

    if (parentSelection) {
      parameters.push({
        key: 'designType',
        value: parentSelection?.designType,
        price: 0,
        discount: 0,
        insPrice: 0,
      });
    }

    if (skipped) {
      parameters.push(...this.currentStep.params);
    }

    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }

    return parameters;
  }
  #getThicknessOptions(parentSelections: any): GetOptions {
    const filteredPackages = this.filterStepPackages(parentSelections);
    const thicknessList = getThickness(
      this.content,
      parentSelections ? filteredPackages : this.allCurrentPackages,
      parentSelections?.designType
    );
    const onThicknessSelected = (
      option: any,
      skipped?: boolean,
      additionalParameters?: any,
      parentSelections?: any
    ) => {
      if (!option?.id) {
        this.goNextStep([]);
        //TODO
        //console.log(no value)
        return;
      }

      const parameters = this.getThicknessParameters(
        option,
        skipped,
        additionalParameters,
        parentSelections
      );
      this.goNextStep(parameters, skipped);
    };
    return [thicknessList, onThicknessSelected];
  }
  // get addons options

  getAntiReflectiveParameters(
    option: any,
    skipped?: boolean,
    labels?: { [key: string]: string }
  ) {
    const addons = getAddons({
      content: this.content,
      allCurrentPackages: this.allCurrentPackages,
      labels,
    });
    const checkIfSelectedIsPremium = (selectedAddOn: any) => {
      let minAddOn = minBy(addons, 'price');
      return selectedAddOn.id !== minAddOn?.['id'];
    };

    let params;
    const addonId = option.id;
    const splittedKey = addonId.split('$');
    const key = splittedKey[0];
    const value = splittedKey[1];

    const titleToShow = this.content?.[key]?.[value]?.title;

    let selectedAddOn = addons.filter((addOn) => {
      return addOn.id === addonId;
    });
    if (value === 'null') {
      params = [
        {
          key: 'addOns',
          value: null,
          price: selectedAddOn[0].price,
          discount: selectedAddOn[0].discount,
          insPrice:
            skipped && selectedAddOn[0].price == 0
              ? 0
              : selectedAddOn[0].insPrice,
          attributes: {
            isPremium: checkIfSelectedIsPremium(selectedAddOn[0]),
          },
        },
      ];
    } else if (value === 'undefined') {
      params = [
        {
          key: 'addOns',
          value: undefined,
          price: selectedAddOn[0].price,
          discount: selectedAddOn[0].discount,
          insPrice:
            skipped && selectedAddOn[0].price == 0
              ? 0
              : selectedAddOn[0].insPrice,
          attributes: {
            isPremium: checkIfSelectedIsPremium(selectedAddOn[0]),
          },
        },
      ];
    } else {
      params = [
        {
          key: 'addOns',
          value: {
            type: key,
            selection: value,
            value: titleToShow,
          },
          price: selectedAddOn[0].price,
          discount: selectedAddOn[0].discount,
          insPrice:
            skipped && selectedAddOn[0].price == 0
              ? 0
              : selectedAddOn[0].insPrice,
          attributes: {
            isPremium: checkIfSelectedIsPremium(selectedAddOn[0]),
          },
        },
      ];
    }
    return params;
  }

  #getAddonsOptions(labels?: { [key: string]: string }): GetOptions {
    const addons = getAddons({
      content: this.content,
      allCurrentPackages: this.allCurrentPackages,
      labels,
    });
    const checkIfSelectedIsPremium = (selectedAddOn: any) => {
      let minAddOn = minBy(addons, 'price');
      return selectedAddOn.id !== minAddOn?.['id'];
    };
    const onAddonsSelected = (option: any, skipped?: boolean) => {
      const addonId = option.id;
      const splittedKey = addonId.split('$');
      const key = splittedKey[0];
      const value = splittedKey[1];

      const titleToShow = this.content?.[key]?.[value]?.title;

      let selectedAddOn = addons.filter((addOn) => {
        return addOn.id === addonId;
      });
      if (value === 'null') {
        this.goNextStep(
          [
            {
              key: 'addOns',
              value: null,
              price: selectedAddOn[0].price,
              discount: selectedAddOn[0].discount,
              insPrice:
                skipped && selectedAddOn[0].price == 0
                  ? 0
                  : selectedAddOn[0].insPrice,
              attributes: {
                isPremium: checkIfSelectedIsPremium(selectedAddOn[0]),
              },
            },
          ],
          skipped
        );
      } else if (value === 'undefined') {
        this.goNextStep(
          [
            {
              key: 'addOns',
              value: undefined,
              price: selectedAddOn[0].price,
              discount: selectedAddOn[0].discount,
              insPrice:
                skipped && selectedAddOn[0].price == 0
                  ? 0
                  : selectedAddOn[0].insPrice,
              attributes: {
                isPremium: checkIfSelectedIsPremium(selectedAddOn[0]),
              },
            },
          ],
          skipped
        );
      } else {
        this.goNextStep(
          [
            {
              key: 'addOns',
              value: {
                type: key,
                selection: value,
                value: titleToShow,
              },
              price: selectedAddOn[0].price,
              discount: selectedAddOn[0].discount,
              insPrice:
                skipped && selectedAddOn[0].price == 0
                  ? 0
                  : selectedAddOn[0].insPrice,
              attributes: {
                isPremium: checkIfSelectedIsPremium(selectedAddOn[0]),
              },
            },
          ],
          skipped
        );
      }
    };
    return [addons, onAddonsSelected];
  }
  // get designType options
  getDesignTypeParameters(
    option: any,
    skipped?: boolean,
    additionalParameters?: any
  ) {
    const id = option.id;
    const priceObj = getPriceStep({ designType: id });
    const addedParams = skipped ? this.currentStep.params : [];
    const parameters = id
      ? [
          {
            key: 'designType',
            price: priceObj?.price,
            discount: priceObj?.discount,
            insPrice: skipped && priceObj?.price == 0 ? 0 : priceObj?.insPrice,
            value: id || null,
          },
          ...addedParams,
        ]
      : [];

    if (additionalParameters) {
      const parametersToAdd = [];
      Object.entries(additionalParameters).forEach(([key, val]) => {
        parametersToAdd.push(...this.getParameters(key, val));
      });
      parameters.push(...parametersToAdd);
    }
    return parameters;
  }
  #getDesignTypeOptions(parentSelections?: any): GetOptions {
    const designTypes = getDesignTypes(this.allCurrentPackages, this.content);
    const onDesignSelected = (option: any, skipped?: boolean) => {
      this.goNextStep(
        this.getDesignTypeParameters(option, skipped, parentSelections),
        skipped
      );
    };

    return [designTypes, onDesignSelected];
  }
  // get included treatments
  getIncludedTreatments({ labels }: { labels: { [key: string]: string } }) {
    const { includedTreatment, treatments } = getIncluded({
      content: this.content,
      allCurrentPackages: this.allCurrentPackages,
      selections: this.getAllCurrentParameterStep(),
      labels,
    });
    return { includedTreatment, treatments };
  }
  setHideCurrentStep(val: boolean) {
    this.hideCurrentStep = val;
  }
  checkOptionsAndGoNext(currentStep: any, dispatch: any) {
    if (currentStep) {
      if (
        !currentStep?.definingAttributes?.skip_step ||
        currentStep.definingAttributes?.skip_step === false
      ) {
        let firstAttributeValue = currentStep?.components?.find(
          (comp: any) => comp?.attribute
        );
        let attributeComponent = currentStep.components.filter(
          (component: any) => component.id === 'RXC_ATTRIBUTE_TILE_LIST'
        );
        let manualComponent = currentStep.components.filter(
          (component: any) => component.id === 'RXC_MANUAL_LIST'
        );

        let colorAttribute = false;
        let attributeOpt = '';
        const hasAttribute = (component: any, attribute: string): boolean => {
          // Controlla se l'attributo del componente è "color"
          if (component?.attribute === attribute) {
            return true;
          }

          // Se il componente ha nestedComponents, controlla ricorsivamente
          if (
            component?.nestedComponents &&
            component?.nestedComponents.length > 0
          ) {
            return component.nestedComponents.some((nestedComponent: any) =>
              hasAttribute(nestedComponent, attribute)
            );
          }

          // Se non c'è l'attributo "color" e non ci sono nestedComponents, ritorna false
          return false;
        };
        const hasAttributeComponent = (
          component: any
        ): { id: string; attribute: string } => {
          if (component?.attribute) {
            return { id: component.id, attribute: component.attribute };
          }

          // Se ha nestedComponents, cerca ricorsivamente un qualsiasi componente con attribute
          if (component?.nestedComponents?.length > 0) {
            for (const nestedComponent of component.nestedComponents) {
              const result = hasAttributeComponent(nestedComponent);
              if (result) {
                return result; // Appena trova un attribute, lo restituisce
              }
            }
          }

          // Nessun componente ha l'attributo
          return { id: '', attribute: '' };
        };
        let findedOption = '';

        //siamo in advancedPrescription
        if (manualComponent.length >= 1) {
          if (
            (this.lensType === 'Non_prescription' ||
              this.lensType === 'Non-prescription') &&
            currentStep?.definingAttributes?.step_id === 'LensPrescription'
          ) {
            window.rxcBrain.goNextStep([], true);
          }
          this.setHideCurrentStep(false);
          return;
        } else {
          //capire nel caso di piu attribute_tile_list quante options ci sono per ogni tile
          if (attributeComponent.length > 1) {
            //se ci sono piu attribute_tile_list controllo se tutti hanno la stessa quantità di options
            let counter = 0;
            attributeComponent.forEach((component: any) => {
              if (component?.attribute) {
                let options = this.getOptions({
                  attribute: colorAttribute ? 'sun' : component?.attribute,
                });
                if (options?.[0]?.length > 0) {
                  counter++;
                  findedOption = component.attribute;
                }
              }
            });
            if (counter > 1) {
              this.setHideCurrentStep(false);
              return;
            }
          } else {
            //se non abbiamo ne attribute_tile_list ne manual_list
            //controllo sui componenti cercando quello con nestedComponents
            let colorComponent = currentStep.components.filter(
              (component: any) => hasAttribute(component, 'color')
            );
            colorAttribute = hasAttribute(colorComponent?.[0], 'color');
            if (
              attributeComponent.length === 0 &&
              manualComponent.length === 0
            ) {
              let nestedComponent = currentStep.components.filter(
                (component: any) => component?.nestedComponents?.length > 0
              );
              if (nestedComponent.length > 0) {
                //ricerco nei vari nestedComponents annidati l'attributo color
                colorAttribute = hasAttribute(nestedComponent[0], 'color');
                if (!colorAttribute) {
                  attributeOpt = hasAttributeComponent(
                    nestedComponent[0]
                  ).attribute;
                }
              }
            }
          }
        }

        //per gli step di treatments e addons le relative proprietà da cercare nei pacchetti
        //hanno nomi diversi dall'attribute dell'attribute_tile_list
        let attributeValue =
          attributeComponent?.[0]?.attribute === 'addons'
            ? 'antiReflective'
            : // : attributeComponent?.[0]?.attribute === 'GVPtreatment'
              //   ? 'treatment'
              findedOption !== ''
              ? findedOption
              : attributeComponent?.[0]?.attribute === 'treatment'
                ? 'transition'
                : attributeComponent?.[0]?.attribute;
        if (colorAttribute) {
          //controllo se devo andare in transitionColor (se c'è)
          if (
            this.stepChain?.find((cs) =>
              cs?.params?.find((p: any) => p.key === 'color')
            )
          ) {
            this.goNextStep([], true);
            return;
          }
          let checkFirstAttribute = true;
          if (firstAttributeValue) {
            let firstAttributeOptions = this?.getOptions({
              attribute: firstAttributeValue?.attribute,
            });
            checkFirstAttribute = !!(
              firstAttributeOptions &&
              firstAttributeOptions.length &&
              firstAttributeOptions?.[0] &&
              firstAttributeOptions[0].length
            );
            if (checkFirstAttribute) {
              let parentSelectionsStep = firstAttributeOptions?.[0]?.map(
                (ao: any) => ({
                  [firstAttributeValue?.attribute]: { id: ao.id },
                })
              );

              const colorsOptionsCheck = parentSelectionsStep
                ?.slice(0, 2)
                .map((step) => this.getSunColors(step));
              const shouldHideFirstComponent = colorsOptionsCheck?.every(
                (options) => options?.[0]?.length > 0
              );

              if (!shouldHideFirstComponent) {
                this.notShowFirstComponent = true;
              }
            }
          }

          const isSunLensType = this.currentPackages?.every(
            (c: LensPackage) => c.lensPackage.lensType === 'Sun'
          );
          const isSunglassesLens = this.currentPackages?.every(
            (c: LensPackage) => sunRule(c.lensPackage)
          );
          if (
            firstAttributeValue.attribute?.includes('transition') &&
            isSunLensType
          ) {
            this.goNextStep([], true);
            return;
          }

          let colorOptions = this.getSunColors();
          if (
            colorOptions &&
            colorOptions?.[0]?.length === 1 &&
            (checkFirstAttribute || !currentStep?.definingAttributes?.autoSkip)
          ) {
            let correctColor = colorOptions?.[0]?.[0];
            let onColorClick = colorOptions?.[1];
            if (correctColor && onColorClick) {
              if (
                !isUndefined(currentStep?.definingAttributes?.autoSkip) &&
                !isNull(currentStep?.definingAttributes?.autoSkip) &&
                !currentStep?.definingAttributes?.autoSkip
              ) {
                this.setHideCurrentStep(false);
                return;
              } else {
                // if transition dispatch transition id (useTransitionImage)

                if (
                  //possiamo ricevere dei pacchetti senza lensType
                  // in quel caso controlliamo che non siano sunglasses
                  (!isSunLensType && !isSunglassesLens) ||
                  this.currentPackages?.every(
                    (c: LensPackage) => c.lensPackage.transition
                  )
                ) {
                  dispatch(colorActions.selectTransition(correctColor.id));

                  // set card image
                  dispatch(
                    colorActions.setCardTransitionImage({
                      imageFileName1: correctColor.imageFileName1,
                      imageFileName2: correctColor.imageFileName2,
                    })
                  );
                }

                dispatch(
                  colorActions.setSwatchUrl({
                    url: getUrlSwatch(
                      correctColor.value,
                      this.flowDefiningAttributes?.imageSrcsetTemplate ||
                        '{url}{filename}@2x.{format} 2x, {url}{filename}@3x.{format} 3x',
                      this.flowDefiningAttributes?.projectImage ||
                        this.config.baseURLs.projectImage
                    ),
                    name: correctColor.name,
                    value: correctColor.value,
                  })
                );

                dispatch(colorActions.selectColor(correctColor.id));
                dispatch(
                  colorActions.setStepColor(
                    currentStep?.definingAttributes.step_id
                  )
                );
                onColorClick(correctColor, true);
              }
            } else {
              this.goNextStep([], true);
            }
          } else if (
            colorOptions &&
            colorOptions?.[0]?.length < 1 &&
            checkFirstAttribute
          ) {
            this.goNextStep([], true);
          } else {
            if (checkFirstAttribute) {
              this.setHideCurrentStep(false);
              return;
            } else {
              this.goNextStep([], true);
            }
          }
        } else {
          //se siamo in protection plan
          if (attributeValue === 'warrantyOptions') {
            if (this.warrantyOptions) {
              // Controllo le soglie di prezzo per mostrare 75$ SV, 85 P, CR RXC3-1505
              let svThreshold = '';
              let progressiveThreshold = '';
              this.warrantyOptions.forEach((wo) => {
                if (
                  (!wo?.type ||
                    wo.type === null ||
                    wo.type?.toLowerCase()?.includes('single')) &&
                  wo?.threshold
                ) {
                  svThreshold = wo.threshold;
                }
                if (wo.type && wo.type === 'Progressive' && wo?.threshold) {
                  progressiveThreshold = wo.threshold;
                }
              });
              const currentPkg = getCheapPackageFromList(this.currentPackages);
              if (currentPkg) {
                if (
                  currentPkg.lensPackage.type === 'Progressive' &&
                  progressiveThreshold
                ) {
                  if (
                    Number.parseFloat(
                      currentPkg.lensPackage?.insPrice ??
                        currentPkg.lensPackage.offerPrice
                    ) +
                      Number.parseFloat(
                        currentPkg.frame?.insPrice ??
                          currentPkg.frame.offerPrice
                      ) >=
                    Number.parseFloat(progressiveThreshold)
                  ) {
                    this.setHideCurrentStep(false);
                    return;
                  } else {
                    this.goNextStep([], true);
                  }
                } else if (svThreshold) {
                  if (
                    Number.parseFloat(
                      currentPkg.lensPackage?.insPrice ??
                        currentPkg.lensPackage.offerPrice
                    ) +
                      Number.parseFloat(
                        currentPkg.frame?.insPrice ??
                          currentPkg.frame.offerPrice
                      ) >=
                    Number.parseFloat(svThreshold)
                  ) {
                    this.setHideCurrentStep(false);
                    return;
                  } else {
                    this.goNextStep([], true);
                  }
                } else {
                  this.setHideCurrentStep(false);
                  return;
                }
              } else {
                this.goNextStep([], true);
              }
            } else {
              this.goNextStep([], true);
            }
          } else {
            if (attributeOpt !== '') {
              attributeValue = attributeOpt;
            }
            const uniqueValues = new Set();
            let allNull = true;
            //nel caso di una singola ATTRIBUTE_TILE_LIST
            //effettuo un filtraggio dei pacchetti in base all'attribute di ATTRIBUTE_TILE_LIST
            this.currentPackages?.forEach((pkg: any) => {
              const value =
                pkg.lensPackage?.[
                  attributeValue === 'blueLightOpt'
                    ? 'blueLight'
                    : attributeValue === 'transitionOpt'
                      ? 'transition'
                      : attributeValue === 'GVPtreatment'
                        ? 'treatment'
                        : attributeValue
                ];
              if (value !== null) {
                allNull = false; // Se troviamo un valore diverso da null, aggiorniamo
              }
              if (value !== undefined) {
                uniqueValues.add(value);
              }
            });
            if (allNull) {
              uniqueValues.add(null);
            }
            //prendo le opzioni disponibili nello step
            let options = this.getOptions({
              attribute:
                attributeValue === 'antiReflective' ? 'addons' : attributeValue,
            });

            let params: Parameter[] = [];

            if (uniqueValues.size === 1) {
              if (
                !isUndefined(currentStep?.definingAttributes?.autoSkip) &&
                !isNull(currentStep?.definingAttributes?.autoSkip) &&
                !currentStep?.definingAttributes?.autoSkip &&
                options?.[0]?.length
              ) {
                this.setHideCurrentStep(false);
                return;
              } // controlliamo se abbiamo dei nested components sotto
              else {
                let nestedComponent = currentStep.components.filter(
                  (component: any) => component?.nestedComponents?.length > 0
                );
                // se abbiamo dei nested components
                if (nestedComponent.length > 0) {
                  //ricerco nei vari nestedComponents annidati l'attributo color
                  const showStep = (components: any) => {
                    for (const c of components) {
                      const { id: nestedId, attribute: nestedAttribute } =
                        hasAttributeComponent(c);
                      if (nestedAttribute) {
                        let cOptions = this.getOptions({
                          attribute:
                            nestedAttribute === 'antiReflective'
                              ? 'addons'
                              : nestedAttribute,
                        });
                        if (
                          cOptions[0] &&
                          cOptions[0]?.length > 1 &&
                          (nestedId === 'RXC_ATTRIBUTE_TILE_LIST' ||
                            nestedId === 'RXC_COLOR_PICKER')
                        ) {
                          return true; // Se troviamo un'opzione con più di un elemento, ritorna true
                        } else {
                          // Se non ci sono opzioni multiple, controlla ricorsivamente i nestedComponents
                          if (cOptions?.[0]?.length) {
                            const option = cOptions?.[0].find(
                              (o) =>
                                o?.id === uniqueValues.values().next().value
                            );
                            //creo il parametro in base all'opzione trovata
                            let param = this.getParameters(
                              nestedAttribute,
                              option ?? cOptions?.[0]?.[0]
                            );
                            params.push(...param);
                          }
                          if (
                            c?.nestedComponents &&
                            c?.nestedComponents.length > 0
                          ) {
                            return showStep(c.nestedComponents);
                          }
                        }
                      }
                    }
                    return false; // Ritorna false se non trovi nessuna opzione valida
                  };

                  const hasNestedWithMultipleOptions = nestedComponent.find(
                    (nc) => showStep(nc.nestedComponents)
                  );

                  if (hasNestedWithMultipleOptions) {
                    this.setHideCurrentStep(false);
                    return;
                  }
                }
              }
              if (options?.[0]?.length) {
                const option = options?.[0].find(
                  (o) => o?.id === uniqueValues.values().next().value
                );
                //creo il parametro in base all'opzione trovata
                let param = this.getParameters(
                  attributeValue,
                  option ?? options?.[0]?.[0]
                );
                params.push(...param);
                let paramKeyCheck =
                  param?.[0]?.key === 'blueLightOpt'
                    ? 'blueLight'
                    : param?.[0]?.key === 'transitionOpt'
                      ? 'transition'
                      : param?.[0]?.key;
                //controllo se il parametro è stato già passato (nested)
                if (
                  this.stepChain.find((step) =>
                    step?.params?.find(
                      (paramT: any) => paramT?.key === paramKeyCheck
                    )
                  ) &&
                  this.currentStep?.params?.find(
                    (paramT: any) => paramT?.key !== paramKeyCheck
                  )
                ) {
                  this.goNextStep([], true);
                } else {
                  this.goNextStep(params, true);
                }
              } else {
                this.goNextStep([], true);
              }
            } else if (uniqueValues.size < 1) {
              //creare il param con la singola opzione dello step da skippare
              this.goNextStep([], true);
            } else {
              this.setHideCurrentStep(false);
            }
          }
        }
      } else {
        this.goNextStep([], true);
      }
    }
  }

  editFromCartGoNext(currentStep: any, dispatch: any) {
    if (currentStep) {
      this.setHideCurrentStep(false);
      if (
        !currentStep?.definingAttributes?.skip_step ||
        currentStep.definingAttributes?.skip_step === false
      ) {
        let attributeComponent = currentStep.components.filter(
          (component: any) => component.id === 'RXC_ATTRIBUTE_TILE_LIST'
        );
        let manualComponent = currentStep.components.filter(
          (component: any) => component.id === 'RXC_MANUAL_LIST'
        );

        let colorAttribute = false;
        let attributeOpt = '';
        const hasAttribute = (component: any, attribute: string): boolean => {
          // Controlla se l'attributo del componente è "color"
          if (component?.attribute === attribute) {
            return true;
          }

          // Se il componente ha nestedComponents, controlla ricorsivamente
          if (
            component?.nestedComponents &&
            component?.nestedComponents.length > 0
          ) {
            return component.nestedComponents.some((nestedComponent: any) =>
              hasAttribute(nestedComponent, attribute)
            );
          }

          // Se non c'è l'attributo "color" e non ci sono nestedComponents, ritorna false
          return false;
        };

        const hasAttributeComponent = (component: any): string => {
          // Controlla se il componente ha l'id "RXC_ATTRIBUTE_TILE_LIST"
          if (component?.attribute) {
            return component.attribute;
          }
          // Se il componente ha nestedComponents, controlla ricorsivamente
          if (component?.nestedComponents?.length > 0) {
            for (const nestedComponent of component.nestedComponents) {
              const result = hasAttributeComponent(nestedComponent);
              if (result) {
                return result;
              }
            }
          }
          // Nessun componente corrisponde
          return '';
        };
        if (
          (attributeComponent?.length > 1 &&
            attributeComponent?.find(
              (ac: any) => ac?.attribute === 'transition'
            )) ||
          ((attributeComponent.length === 0 ||
            (attributeComponent?.length === 1 &&
              attributeComponent?.[0]?.attribute === 'treatment')) &&
            manualComponent.length === 0)
        ) {
          let nestedComponent = currentStep.components.filter(
            (component: any) => component?.nestedComponents?.length > 0
          );
          if (nestedComponent.length > 0) {
            //ricerco nei vari nestedComponents annidati l'attributo color
            colorAttribute = hasAttribute(nestedComponent[0], 'color');
            attributeOpt = hasAttributeComponent(nestedComponent[0]);
          }
        }
        let findedOption = '';
        let attributeValue =
          attributeComponent?.[0]?.attribute === 'addons'
            ? 'antiReflective'
            : findedOption !== ''
              ? findedOption
              : attributeComponent?.[0]?.attribute;
        colorAttribute;

        if (colorAttribute && this.packageFromCart?.lensPackage?.['color']) {
          let packageColor = this.packageFromCart?.lensPackage?.['color'];
          let transitionId = this.packageFromCart?.lensPackage?.['transition'];
          const hasSun = this.stepChain.find((c) =>
            c.params.find((p) => p && p.value === 'sun')
          );
          if (
            hasSun &&
            currentStep.definingAttributes.step_id.toLowerCase() !==
              'lenscolour'
          ) {
            this.goNextStep([], true);
          } else {
            if (packageColor) {
              let colorOptions = this.getSunColors();
              //controllo se nei params ho già color
              let colorParam = this.stepChain.filter((step: any) =>
                step.params.find((value: any) => value.key === 'color')
              );
              let transitionParam = this.stepChain.filter((step: any) =>
                step.params.find((value: any) => value.key === 'transition')
              );
              if (
                transitionParam?.length > 0 &&
                this.currentStep?.key === 'LensTreatmentDetail'
              ) {
                this.goNextStep([], true);
              } else {
                if (colorOptions && colorParam.length < 1) {
                  let correctColor = colorOptions?.[0]?.find(
                    (color: any) => color?.id === packageColor
                  );
                  let onColorClick = colorOptions?.[1];
                  if (
                    packageColor &&
                    transitionId &&
                    transitionParam?.length === 0
                  ) {
                    let transOptions = this?.getTreatments('');
                    let transOpt = transOptions?.[0]?.filter(
                      (value: any) =>
                        (value?.id?.indexOf(transitionId) > 0 &&
                          value?.id?.split('$')?.[1] === transitionId) ||
                        value?.id === transitionId
                    );
                    let transParam = this.getTreatmentsParameters(
                      transOpt?.[0]
                    );
                    let ts = { transition: { id: transOpt?.[0]?.['id'] } };
                    //@ts-ignore
                    onColorClick(correctColor, false, ts);

                    if (transitionRule(this.packageFromCart)) {
                      dispatch(colorActions.selectTransition(correctColor?.id));

                      // set card image
                      dispatch(
                        colorActions.setCardTransitionImage({
                          imageFileName1: correctColor?.imageFileName1,
                          imageFileName2: correctColor?.imageFileName2,
                        })
                      );
                    }

                    dispatch(colorActions.selectColor(correctColor?.id));
                    dispatch(
                      colorActions.setStepColor(
                        currentStep?.definingAttributes.step_id
                      )
                    );

                    dispatch(
                      colorActions.setSwatchUrl({
                        url: getUrlSwatch(
                          correctColor?.value,
                          this.flowDefiningAttributes?.imageSrcsetTemplate ||
                            '{url}{filename}@2x.{format} 2x, {url}{filename}@3x.{format} 3x',
                          this.flowDefiningAttributes?.projectImage ||
                            this.config.baseURLs.projectImage
                        ),
                        name: correctColor?.name,
                        value: correctColor?.value,
                      })
                    );
                    if (this.packageFromCart?.lensPackage.brand) {
                      dispatch?.(
                        colorActions.setLensBrand(
                          this.packageFromCart?.lensPackage.brand
                        )
                      );
                    }
                    dispatch?.(colorActions.setClearClen(''));
                  } else {
                    if (colorOptions?.[0] && colorOptions[0].length > 1) {
                      onColorClick(correctColor);
                    } else {
                      onColorClick(correctColor, true);
                    }

                    if (transitionRule(this.packageFromCart)) {
                      dispatch(colorActions.selectTransition(correctColor?.id));

                      // set card image
                      dispatch(
                        colorActions.setCardTransitionImage({
                          imageFileName1: correctColor?.imageFileName1,
                          imageFileName2: correctColor?.imageFileName2,
                        })
                      );
                    }

                    dispatch(colorActions.selectColor(correctColor?.id));
                    dispatch(
                      colorActions.setStepColor(
                        currentStep?.definingAttributes.step_id
                      )
                    );

                    dispatch(
                      colorActions.setSwatchUrl({
                        url: getUrlSwatch(
                          correctColor?.value,
                          this.flowDefiningAttributes?.imageSrcsetTemplate ||
                            '{url}{filename}@2x.{format} 2x, {url}{filename}@3x.{format} 3x',
                          this.flowDefiningAttributes?.projectImage ||
                            this.config.baseURLs.projectImage
                        ),
                        name: correctColor?.name,
                        value: correctColor?.value,
                      })
                    );

                    if (this.packageFromCart?.lensPackage.brand) {
                      dispatch?.(
                        colorActions.setLensBrand(
                          this.packageFromCart?.lensPackage.brand
                        )
                      );
                    }

                    dispatch?.(colorActions.setClearClen(''));
                  }
                } else {
                  this.goNextStep([], true);
                }
              }
            } else {
              this.goNextStep([], true);
            }
          }
        } else {
          //caso advancedPrescription step
          if (manualComponent.length >= 1) {
            if (
              (this.lensType === 'Non_prescription' ||
                this.lensType === 'Non-prescription') &&
              currentStep?.definingAttributes?.step_id === 'LensPrescription'
            ) {
              window.rxcBrain.goNextStep([], true);
            } else {
              let prescription = this?.currentPrescription;
              let reviewTitle = '';
              // Logica per la selezione del titolo da visualizzare in review
              switch (prescription?.prescriptionFlow) {
                case 'MANUAL':
                  reviewTitle = manualComponent?.[0]?.manualCardReviewTitle;
                  break;

                case 'UPLOAD':
                case 'NESTED':
                  reviewTitle = manualComponent?.[0]?.uploadCardReviewTitle;
                  break;

                case 'CMD':
                  reviewTitle =
                    manualComponent?.[0]?.callMyDoctorCardReviewTitle;
                  break;

                case 'LATER':
                  reviewTitle =
                    manualComponent?.[0]?.sendItLaterCardReviewTitle;
                  break;

                default:
                  // Caso di default se nessuno degli altri matcha
                  console.log('Caso di default: Prescription non riconosciuto');
                  break;
              }
              if (!prescription) {
                reviewTitle = manualComponent?.[0]?.sendItLaterCardReviewTitle;
              }
              this.goNextStep([
                ...this.getPrevCurrentParameterStep(),
                {
                  key: 'advancedPrescription',
                  value: reviewTitle,
                },
              ]);
            }
          } else {
            //Caso più attribute tile list
            if (attributeComponent.length > 1) {
              //se ci sono piu attribute_tile_list controllo se tutti hanno la stessa quantità di options
              let counter = 0;
              attributeComponent.forEach((component: any) => {
                if (component?.attribute) {
                  let tFamilyOptions = this.getOptions({
                    attribute: component?.attribute,
                  });
                  if (tFamilyOptions?.[0]?.length > 0) {
                    counter++;
                    findedOption = component.attribute;
                  }
                }
              });
              //se abbiamo più di una option disponibile bisogna capire il pacchetto del carrello
              //che proprietà ha andando a fare una priorità su transition > blueLight > clear
              if (counter > 1 || !currentStep?.definingAttributes?.autoSkip) {
                let sunCheck =
                  brain?.packageFromCart?.lensPackage?.['color'] &&
                  !brain?.packageFromCart?.lensPackage?.['transition'];
                let transitionCheck =
                  brain?.packageFromCart?.lensPackage?.['transition'];
                let blueLightCheck =
                  brain?.packageFromCart?.lensPackage?.['blueLight'];
                if (sunCheck) {
                  const sunAttributeComponent = attributeComponent.find((ac) =>
                    hasAttribute(ac, 'sun')
                  );
                  const labels = {
                    transitionTitle: sunAttributeComponent?.sunTitleReview,
                  };
                  let sunOptions = brain.getOptions({
                    attribute: 'sun',
                    labels: labels,
                  });
                  let clearOptions = brain.getOptions({
                    attribute: 'clear',
                    labels: labels,
                  });
                  let transitionOptions = brain.getOptions({
                    attribute: 'transition',
                    labels: labels,
                  });
                  let blueLightOptions = brain.getOptions({
                    attribute: 'blueLight',
                    labels: labels,
                  });

                  let onAttributeSelected = sunOptions?.[1];
                  onAttributeSelected(
                    sunOptions?.[0]?.[0],
                    !blueLightOptions?.[0]?.length &&
                      !transitionOptions?.[0]?.length &&
                      !clearOptions?.[0]?.length
                  );
                } else if (transitionCheck) {
                  const transitionAttributeComponent = attributeComponent.find(
                    (ac) => hasAttribute(ac, 'transition')
                  );
                  const labels = {
                    transitionTitle:
                      transitionAttributeComponent?.transitionTitleReview,
                  };
                  let transitionOptions = brain.getOptions({
                    attribute: 'transition',
                    labels: labels,
                  });
                  let onAttributeSelected = transitionOptions?.[1];
                  let correctOption = transitionOptions?.[0]?.find(
                    (opt: any) => opt?.id?.split('$')?.[1] === transitionCheck
                  );
                  onAttributeSelected(
                    correctOption ? correctOption : transitionOptions?.[0]?.[0]
                  );
                } else if (blueLightCheck) {
                  const blueLightAttributeComponent = attributeComponent.find(
                    (ac) => hasAttribute(ac, 'blueLight')
                  );
                  const labels = {
                    blueLightTitle:
                      blueLightAttributeComponent?.blueLightTitleReview,
                  };
                  let blueLightOptions = brain.getOptions({
                    attribute: 'blueLight',
                    labels: labels,
                  });
                  let onAttributeSelected = blueLightOptions?.[1];
                  //se ho più opzioni per blueLight
                  if (blueLightOptions?.[0]?.length > 1) {
                    let blueLightChosenOption = blueLightOptions?.[0]?.filter(
                      (opt: any) => {
                        let id = opt?.id.replace(/^.*\$/, '');
                        return (
                          id ===
                          brain?.packageFromCart?.lensPackage?.['blueLight']
                        );
                      }
                    );
                    onAttributeSelected(blueLightChosenOption[0]);
                  } else {
                    onAttributeSelected(blueLightOptions?.[0]?.[0]);
                  }
                } else {
                  console.log('##', attributeComponent);
                  const clearAttributeComponent = attributeComponent.find(
                    (ac) => hasAttribute(ac, 'clear')
                  );
                  const labels = {
                    clearTitle: clearAttributeComponent?.clearTitleReview,
                  };
                  let clearOptions = brain.getOptions({
                    attribute: 'clear',
                    labels: labels,
                  });
                  let onAttributeSelected = clearOptions?.[1];
                  onAttributeSelected(clearOptions?.[0]?.[0]);
                }
              } else if (counter === 1) {
                //se abbiamo una sola tile disponibile per uno step con più attribute tile list la autoselezioniamo

                // REMOVED BECAUSE IN REVIEW WE DONT WANT TO SEE IT
                // let tFamilyOption = this.getOptions({
                //   attribute: findedOption,
                // });
                // let tFamilyParam = this.getParameters(
                //   findedOption,
                //   tFamilyOption?.[0]?.[0]
                // );
                this.goNextStep([], true);
              }
            } else {
              //Caso singola attribute tile list
              let options;
              if (attributeValue === 'warrantyOptions') {
                options = this.getOptions({
                  attribute: attributeValue,
                  labels: {
                    protectionPlanAsIs:
                      'No, proceed without the Protection Plan',
                  },
                });
              } else {
                if (attributeOpt) {
                  attributeValue = attributeOpt;
                }
                options = this.getOptions({
                  attribute: colorAttribute
                    ? 'sun'
                    : attributeValue === 'antiReflective'
                      ? 'addons'
                      : attributeValue,
                });
              }
              /* TODO: FIX */
              let nestedComponent = currentStep.components.filter(
                (component: any) => component?.nestedComponents?.length > 0
              );
              const cartPackage: any = this?.packageFromCart?.lensPackage;
              if (
                nestedComponent.length > 0 &&
                cartPackage &&
                attributeValue !== 'lensBundle' &&
                options?.[0]?.length > 0
              ) {
                let nestedParams: any[] = [];
                nestedComponent.forEach((nc) => {
                  const attributeNc = hasAttributeComponent(nc);
                  if (
                    attributeNc &&
                    cartPackage[
                      attributeNc === 'GVPtreatment' ? 'treatment' : attributeNc
                    ]
                  ) {
                    let o = this.#getGVPTreatmentsOptions(null);
                    let correctO = o?.[0]?.filter(
                      (opt: any) => opt?.id === cartPackage['treatment']
                    );
                    nestedParams.push({
                      key: attributeNc,
                      value:
                        cartPackage[
                          attributeNc === 'GVPtreatment'
                            ? 'treatment'
                            : attributeNc
                        ],
                      title: correctO?.[0]?.title,
                    });
                  }

                  // do the same thing for nested
                  nc?.nestedComponents.forEach((nnc) => {
                    const attributeNestedNc = hasAttributeComponent(nnc);

                    if (attributeNestedNc && cartPackage[attributeNestedNc]) {
                      nestedParams.push({
                        key: attributeNestedNc,
                        value: cartPackage[attributeNestedNc],
                      });
                    }
                  });
                });
                if (nestedParams && nestedParams?.length) {
                  this.goNextStep([
                    // ...this.getPrevCurrentParameterStep(),
                    ...nestedParams,
                  ]);
                }
              } else if (options?.[0]?.length > 0) {
                // far si che venga chiamata la "onAttributeSelected" che gestirà l'avanzamento
                // passando ogni volta il valore del pacchetto che arriva dal carrello
                const onAttributeSelected = options?.[1]; // funzione da chiamare
                let selectedOption;
                let packageAttribute = null;
                if (attributeValue === 'treatment') {
                  packageAttribute =
                    this.packageFromCart?.lensPackage?.['transition'] ?? null;
                } else if (attributeValue === 'blueLightOpt') {
                  packageAttribute =
                    this.packageFromCart?.lensPackage?.['blueLight'] ?? null;
                } else if (attributeValue === 'transitionOpt') {
                  packageAttribute =
                    this.packageFromCart?.lensPackage?.['transition'] ?? null;
                } else {
                  packageAttribute =
                    this.packageFromCart?.lensPackage?.[attributeValue] ?? null;
                }
                //se ci troviamo in protection plan
                if (options[1] && attributeValue === 'warrantyOptions') {
                  const preselectedOption =
                    this.config.data?.warranty?.catEntryId;
                  if (this.warrantyOptions) {
                    // Controllo le soglie di prezzo per mostrare 75$ SV, 85 P, CR RXC3-1505
                    let svThreshold = '';
                    let progressiveThreshold = '';
                    this.warrantyOptions.forEach((wo) => {
                      if (
                        (!wo?.type ||
                          wo.type === null ||
                          wo.type?.toLowerCase()?.includes('single')) &&
                        wo?.threshold
                      ) {
                        svThreshold = wo.threshold;
                      }
                      if (
                        wo.type &&
                        wo.type === 'Progressive' &&
                        wo?.threshold
                      ) {
                        progressiveThreshold = wo.threshold;
                      }
                    });
                    const currentPkg = this.packageFromCart;
                    if (currentPkg) {
                      if (
                        currentPkg.lensPackage.type === 'Progressive' &&
                        progressiveThreshold
                      ) {
                        if (
                          Number.parseFloat(
                            currentPkg.lensPackage?.insPrice ??
                              currentPkg.lensPackage.offerPrice
                          ) +
                            Number.parseFloat(
                              currentPkg.frame?.insPrice ??
                                currentPkg.frame.offerPrice
                            ) >=
                          Number.parseFloat(progressiveThreshold)
                        ) {
                          if (
                            preselectedOption !== null &&
                            preselectedOption !== undefined
                          ) {
                            onAttributeSelected({ id: preselectedOption });
                          } else {
                            onAttributeSelected({ id: 'asIs' });
                          }
                        } else {
                          this.goNextStep([], true);
                        }
                      } else if (svThreshold) {
                        if (
                          Number.parseFloat(
                            currentPkg.lensPackage?.insPrice ??
                              currentPkg.lensPackage.offerPrice
                          ) +
                            Number.parseFloat(
                              currentPkg.frame?.insPrice ??
                                currentPkg.frame.offerPrice
                            ) >=
                          Number.parseFloat(svThreshold)
                        ) {
                          if (
                            preselectedOption !== null &&
                            preselectedOption !== undefined
                          ) {
                            onAttributeSelected({ id: preselectedOption });
                          } else {
                            onAttributeSelected({ id: 'asIs' });
                          }
                        } else {
                          this.goNextStep([], true);
                        }
                      } else {
                        if (
                          preselectedOption !== null &&
                          preselectedOption !== undefined
                        ) {
                          onAttributeSelected({ id: preselectedOption });
                        } else {
                          onAttributeSelected({ id: 'asIs' });
                        }
                      }
                    } else {
                      this.goNextStep([], true);
                    }
                  } else {
                    this.goNextStep([], true);
                  }
                } else {
                  const uniqueValues = new Set();
                  let allNull = true;
                  //nel caso di una singola ATTRIBUTE_TILE_LIST
                  //effettuo un filtraggio dei pacchetti in base all'attribute di ATTRIBUTE_TILE_LIST
                  this.currentPackages?.forEach((pkg: any) => {
                    const value =
                      pkg.lensPackage?.[
                        attributeValue === 'blueLightOpt'
                          ? 'blueLight'
                          : attributeValue === 'transitionOpt'
                            ? 'transition'
                            : // : attributeValue === 'GVPtreatment'
                              //   ? 'treatment'
                              colorAttribute === false
                              ? attributeValue
                              : 'color'
                      ];
                    if (value !== null) {
                      allNull = false; // Se troviamo un valore diverso da null, aggiorniamo
                    }
                    if (value !== undefined) {
                      uniqueValues.add(value);
                    }
                  });
                  if (allNull) {
                    uniqueValues.add(null);
                  }
                  // Filtra o seleziona i valori unici come preferisci, ad esempio il primo valore:
                  uniqueValues.forEach((value: any) => {
                    if (packageAttribute === undefined) {
                      packageAttribute = 'null';
                    }
                    if (packageAttribute === value) {
                      //controllo sul type [reading distance]
                      if (value?.toLowerCase().includes('single')) {
                        if (
                          options?.[0]?.find(
                            (opt: any) =>
                              opt?.id &&
                              (opt.id?.toLowerCase()?.includes('distance') ||
                                opt.id?.toLowerCase()?.includes('reading'))
                          )
                        ) {
                          if (
                            (!this.currentPrescription?.ADD_OD &&
                              !this.currentPrescription?.ADD_OS) ||
                            !this.currentPrescription?.ADD
                          ) {
                            packageAttribute = 'Distance';
                          } else {
                            packageAttribute = 'Reading';
                          }
                        } else {
                          packageAttribute = 'Single Vision';
                        }
                      }
                      //controllo se siamo in transition per effettuare il check sul campo transitionId del lensPackage
                      if (attributeValue === 'treatment') {
                        selectedOption = options?.[0];
                      } else {
                        //filtrare le options in base al valore trovato
                        selectedOption = options?.[0].filter((option: any) =>
                          option?.id
                            .toLowerCase()
                            .includes(
                              packageAttribute?.toLowerCase() !== undefined
                                ? packageAttribute?.toLowerCase()
                                : 'null'
                            )
                        );
                      }
                    }
                  });

                  if (attributeValue === 'lensBundle') {
                    let upc = this.packageFromCart?.lensPackage?.upc;
                    let correctBundle = this?.config?.lensesData?.bundles?.find(
                      (bundle: any) => bundle?.lensPackageUPC === upc
                    );
                    if (
                      correctBundle &&
                      this?.config?.lensesData?.bundles?.length > 0
                    ) {
                      console.log('BUNDLE CORRETTO: ' + correctBundle);
                      let correctOption = options?.[0]?.filter(
                        (option: any) =>
                          option?.id === correctBundle?.bundleName
                      );
                      onAttributeSelected(correctOption?.[0], false);
                    } else {
                      onAttributeSelected({}, true);
                    }
                  }
                  // Se c'è un valore selezionato, chiama la funzione onAttributeSelected
                  if (selectedOption?.length > 0 && selectedOption?.[0]) {
                    let value;
                    if (attributeValue === 'treatment') {
                      let transitionId =
                        this.packageFromCart?.lensPackage?.[transition];
                      value = {
                        ...selectedOption[0],
                        transitionId: transitionId,
                      };
                      onAttributeSelected(value);
                    } else {
                      //controllo se l'attributo è thickness
                      //se il pacchetto ha un designType
                      //e se il designType non è step standalone
                      //e se lo step (sarà thickness) ha il designType tra i nestedComponents
                      if (
                        attributeValue === 'thickness' &&
                        this?.packageFromCart?.lensPackage?.designType &&
                        !this?.stepChain?.find(
                          (step: any) => step?.key === 'LensDesign'
                        ) &&
                        hasAttribute(currentStep.components[1], 'designType')
                      ) {
                        const designTypes = (options = this.getOptions({
                          attribute: 'designType',
                        }));

                        onAttributeSelected(
                          selectedOption?.[0],
                          designTypes[0]?.length && designTypes[0]?.length > 1
                            ? false
                            : true,
                          //@ts-ignore
                          {},
                          designTypes[0]?.length &&
                            designTypes[0]?.length > 1 && {
                              designType:
                                this?.packageFromCart?.lensPackage?.designType,
                            }
                        );
                      } else {
                        //se ho una sola opzione nello step passo skipped = true
                        if (options?.[0]?.length === 1) {
                          onAttributeSelected(selectedOption?.[0], true);
                        } else {
                          onAttributeSelected(selectedOption?.[0], false);
                        }
                      }
                    }
                  } else {
                    this.goNextStep([], true);
                  }
                }
              } else {
                this.goNextStep([], true);
              }
            }
          }
        }
      } else {
        this.goNextStep([], true);
      }
    }
  }
  // get steps selections
  getStepSelections() {
    return getStepSelections(true, this.content);
  }

  combinePackagesandPrices(frameCatEntryId: string) {
    if (this.config && this.config.actionsModule) {
      const getPackages = this.config.actionsModule.getPackages;
      const getPrices = this.config.actionsModule.getPrices;
      if (getPackages) {
        let packages = getPackages(frameCatEntryId);
        if (
          !packages.every((pkg: any) => !pkg?.lensPackage?.listPrice) &&
          getPrices !== null &&
          getPrices !== undefined
        ) {
          let prices = getPrices();
          //
        }
      }
    }
  }
}

const brain = new Brain();

export default brain;
